import { db, storage, auth, functions } from "../firebaseConfig";
import { handleAuthError } from "../src/utils/authInterceptor";

/**
 * Customers that belong to OTHER apps sharing the same Stripe account.
 * These names are excluded from all application/transaction queries
 * so the appliance rental app only shows its own customers.
 * Matching is case-insensitive and uses startsWith for partial matches.
 */
const EXCLUDED_CUSTOMER_NAMES = [
  'ask godswill inc',
  'godswill o',
  'chantell latisha booker-tharpe',
  'redmanna inc',
  'redmanna enterprises inc',
  'erica curtis',
  'godswill olemgbe',
  'sample customer',
  'test test',
];

const isExcludedCustomer = (name: string | undefined | null): boolean => {
  if (!name) return false;
  const n = name.trim().toLowerCase();
  return EXCLUDED_CUSTOMER_NAMES.some(exc => n === exc || n.startsWith(exc));
};
import { emailService } from "./emailService";
import { generateApplianceLease, generateApplianceLeaseHTML, ApplianceLeaseData, ApplianceType, getDefaultEquipment } from "../src/templates/applianceLeaseTemplate";
import emailjs from '@emailjs/browser';
import jsPDF from 'jspdf';
import { initializeApp, getApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  deleteDoc,
  Query
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import {
  onAuthStateChanged,
  User,
  signInWithEmailAndPassword
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { Application, AppStatus, InventoryItem, Ticket, Transaction, MaintenanceEvent, CustomerReport } from "../types";

// Helper to access the config from the default app for the secondary app
// @ts-ignore
const firebaseConfig = getApp().options;

// --- File Upload Service ---
export const uploadFile = async (file: File, folder: string): Promise<string> => {
  if (!file) return "";
  const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// --- Database Service ---

export const backendService = {

  monitorAuthState: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },

  // Check if user is admin by looking up in admins collection
  isAdmin: async (uid: string): Promise<boolean> => {
    try {
      const adminDoc = await getDoc(doc(db, "admins", uid));
      return adminDoc.exists();
    } catch (e) {
      console.error("Error checking admin status:", e);
      return false;
    }
  },

  // Check if user is staff by looking up in staff collection
  isStaff: async (uid: string): Promise<boolean> => {
    try {
      const staffDoc = await getDoc(doc(db, "staff", uid));
      return staffDoc.exists();
    } catch (e) {
      console.error("Error checking staff status:", e);
      return false;
    }
  },

  // Get company by URL slug for referral link routing
  getCompanyBySlug: async (slug: string): Promise<any | null> => {
    try {
      // Query companies collection by slug field
      const q = query(collection(db, "companies"), where("slug", "==", slug));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Fallback: Check if slug matches company ID directly
        const companyDoc = await getDoc(doc(db, "companies", slug));
        if (companyDoc.exists()) {
          return { id: companyDoc.id, ...companyDoc.data() };
        }
        return null;
      }

      const companyDoc = snapshot.docs[0];
      return { id: companyDoc.id, ...companyDoc.data() };
    } catch (e) {
      console.error("Error fetching company by slug:", e);
      return null;
    }
  },

  // Submit a lead for mailing list and service area check
  submitLead: async (data: {
    name: string;
    email: string;
    phone: string;
    zipCode: string;
    source?: string;
  }): Promise<{ leadId: string; serviceAreaMatch: any | null }> => {
    try {
      // Check service area match
      const serviceAreaMatch = await backendService.checkServiceArea(data.zipCode);

      // Save lead to Firestore
      const leadDoc = await addDoc(collection(db, "leads"), {
        name: data.name,
        email: data.email.toLowerCase(),
        phone: data.phone,
        zipCode: data.zipCode,
        source: data.source || 'organic',
        status: 'NEW',
        serviceAreaMatch: serviceAreaMatch?.companyId || null,
        forwardedTo: null,
        createdAt: new Date().toISOString()
      });

      return {
        leadId: leadDoc.id,
        serviceAreaMatch
      };
    } catch (e) {
      console.error("Error submitting lead:", e);
      throw new Error("Failed to submit lead. Please try again.");
    }
  },

  // Check if a zip code is in any company's service area
  checkServiceArea: async (zipCode: string): Promise<any | null> => {
    try {
      // Get all service areas
      const snapshot = await getDocs(collection(db, "service_areas"));

      for (const areaDoc of snapshot.docs) {
        const area = areaDoc.data();
        if (area.zipCodes && area.zipCodes.includes(zipCode) && area.isActive !== false) {
          // Found matching area, get company details
          const company = await backendService.getCompanyBySlug(area.companyId);
          if (company) {
            return {
              companyId: area.companyId,
              companyName: company.name,
              companySlug: company.slug || area.companyId
            };
          }
        }
      }

      return null; // No service area match
    } catch (e) {
      console.error("Error checking service area:", e);
      return null;
    }
  },

  // Get all leads (Super Admin only)
  getAllLeads: async (): Promise<any[]> => {
    try {
      const q = query(collection(db, "leads"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error("Error fetching leads:", e);
      return [];
    }
  },

  // Update lead status
  updateLead: async (leadId: string, updates: any): Promise<void> => {
    await updateDoc(doc(db, "leads", leadId), {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  },

  // Forward lead to a company
  forwardLead: async (leadId: string, companyId: string): Promise<void> => {
    await updateDoc(doc(db, "leads", leadId), {
      forwardedTo: companyId,
      status: 'FORWARDED',
      forwardedAt: new Date().toISOString()
    });
  },

  // Add a user to the admins collection
  addAdmin: async (uid: string, email: string): Promise<void> => {
    await setDoc(doc(db, "admins", uid), { email, addedAt: new Date().toISOString() });
  },

  // Get all staff members
  getStaff: async (): Promise<any[]> => {
    const querySnapshot = await getDocs(collection(db, "staff"));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Register a new Staff member
  registerStaffByAdmin: async (data: { email: string; password: string; name: string; role: string }) => {
    // 1. Initialize a secondary app instance with unique name to avoid conflicts
    const appName = `SecondaryAppStaff_${Date.now()}`;
    // @ts-ignore
    const secondaryApp = initializeApp(firebaseConfig, appName);
    const secondaryAuth = getAuth(secondaryApp);

    try {
      // 2. Create the user in the secondary auth
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, data.email, data.password);
      const uid = userCredential.user.uid;

      // 3. Create entry in 'staff' collection
      await setDoc(doc(db, "staff", uid), {
        email: data.email,
        name: data.name,
        role: 'STAFF',
        title: data.role || 'Staff Member',
        addedAt: new Date().toISOString()
      });

      // 4. Send Welcome Email
      const loginUrl = 'https://www.nestlysuite.com/#/admin-login';
      const welcomeHtml = backendService.generateEmailHtml(
        'Welcome to Nestly Suite Team',
        `<p>Dear ${data.name},</p>
         <p>An administrative account has been created for you.</p>
         <p><strong>Username:</strong> ${data.email}</p>
         <p><strong>Password:</strong> ${data.password}</p>
         <p>Please log in and change your password immediately.</p>`,
        loginUrl,
        'Staff Login'
      );

      await backendService.triggerEmail(data.email, "Welcome to Nestly Suite - Staff Access", welcomeHtml);

      // 5. Cleanup
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      return { uid, ...data };

    } catch (error) {
      await deleteApp(secondaryApp);
      throw error;
    }
  },

  // Helper to generate professional HTML email
  generateEmailHtml: (title: string, bodyContent: string, actionLink?: string, actionText?: string) => {
    return `
      <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0;">
        <!-- Header -->
        <div style="background-color: #4f46e5; padding: 30px; text-align: center;">
          <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 600; letter-spacing: 0.5px;">Nestly Suite</h1>
        </div>
        
        <!-- Content -->
        <div style="padding: 40px 30px; color: #334155; line-height: 1.6;">
          <h2 style="color: #1e293b; margin-top: 0; margin-bottom: 20px; font-size: 20px;">${title}</h2>
          <div style="font-size: 16px;">
            ${bodyContent}
          </div>
          
          ${actionLink && actionText ? `
            <div style="margin-top: 30px; text-align: center;">
              <a href="${actionLink}" style="display: inline-block; background-color: #4f46e5; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 600; font-size: 16px;">${actionText}</a>
            </div>
          ` : ''}
        </div>
        
        <!-- Footer -->
        <div style="background-color: #f8fafc; padding: 20px; text-align: center; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b;">
          <p style="margin: 0 0 10px;">&copy; ${new Date().getFullYear()} Nestly Suite Inc. All rights reserved.</p>
          <p style="margin: 0;">Questions? Contact us at <a href="mailto:support@nestlysuite.com" style="color: #4f46e5; text-decoration: none;">support@nestlysuite.com</a></p>
        </div>
      </div>
    `;
  },

  // --- MAINTENANCE & SCHEDULER ---

  getMaintenanceEvents: async (startDate?: string, endDate?: string): Promise<MaintenanceEvent[]> => {
    let q: Query = collection(db, 'maintenance_events');

    if (startDate) {
      q = query(q, where('date', '>=', startDate));
    }
    if (endDate) {
      q = query(q, where('date', '<=', endDate), orderBy('date', 'asc'));
    } else {
      q = query(q, orderBy('date', 'asc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MaintenanceEvent));
  },

  createMaintenanceEvent: async (event: Omit<MaintenanceEvent, 'id'>): Promise<string> => {
    const docRef = await addDoc(collection(db, 'maintenance_events'), {
      ...event,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  updateMaintenanceEvent: async (id: string, updates: Partial<MaintenanceEvent>): Promise<void> => {
    const docRef = doc(db, 'maintenance_events', id);
    await updateDoc(docRef, updates);
  },

  deleteMaintenanceEvent: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'maintenance_events', id));
  },

  // --- END MAINTENANCE ---

  triggerEmail: async (to: string, subject: string, html: string, customerId?: string) => {
    try {
      // Primary: Use SMTP with tracking via Cloud Function
      const result = await emailService.sendEmail({
        to,
        subject,
        html,
        customerId,
        category: 'transactional'
      });

      if (result.success) {
        console.log('✅ Email sent via SMTP to:', to, 'MessageId:', result.messageId);

        // Non-blocking: store in mail collection for record keeping
        // This is just a local log — the email is already sent via the cloud function
        addDoc(collection(db, "mail"), {
          to: [to],
          message: { subject, html },
          delivery: { state: "SENT", provider: "smtp", messageId: result.messageId },
          createdAt: new Date().toISOString(),
          customerId: customerId || null
        }).catch(mailErr => console.warn('Mail record-keeping write failed (non-critical):', mailErr.message));

        return;
      }

      throw new Error(result.error || 'SMTP send failed');

    } catch (e: any) {
      console.error("SMTP failed, falling back to EmailJS:", e.message);

      // Fallback: Try EmailJS
      try {
        emailjs.init('NasyMqvycYpxhct60');
        const result = await emailjs.send(
          'service_nestlysuite',
          'template_s3nioe3',
          {
            to_email: to,
            subject: subject,
            message: html,
            from_name: 'NestlySuite',
            reply_to: 'info@nestlysuite.com'
          }
        );
        console.log('Email sent via EmailJS fallback to:', to);

        // Non-blocking: store in mail collection for record keeping
        addDoc(collection(db, "mail"), {
          to: [to],
          message: { subject, html },
          delivery: { state: "SENT", provider: "emailjs" },
          createdAt: new Date().toISOString(),
          customerId: customerId || null
        }).catch(mailErr => console.warn('Mail record-keeping write failed (non-critical):', mailErr.message));

      } catch (emailjsError: any) {
        console.error("EmailJS also failed:", emailjsError);

        // Last resort: queue to mail collection for Firebase Extension
        addDoc(collection(db, "mail"), {
          to: [to],
          message: { subject, html },
          delivery: { attempts: 0, state: "PENDING" },
          createdAt: new Date().toISOString(),
          customerId: customerId || null
        }).catch(mailErr => console.warn('Mail queue write failed (non-critical):', mailErr.message));
        console.log('Email queued to mail collection as final fallback');
      }
    }
  },

  // --- CRM Messaging ---
  sendBroadcast: async (data: { subject: string; body: string; recipients: string[] }): Promise<void> => {
    const broadcastDoc = await addDoc(collection(db, "broadcasts"), {
      subject: data.subject,
      body: data.body,
      sentAt: new Date().toISOString(),
      recipientCount: data.recipients.length
    });
    // Send to each recipient using EmailJS
    const htmlContent = backendService.generateEmailHtml(
      data.subject,
      `<p>${data.body}</p>`,
      undefined,
      undefined
    );
    for (const email of data.recipients) {
      await backendService.triggerEmail(email, data.subject, htmlContent);
    }
    console.log(`Broadcast sent to ${data.recipients.length} recipients`);
  },

  sendReminder: async (data: { customerId: string; type: string; email: string; name: string }): Promise<void> => {
    const reminderMessages: { [key: string]: { subject: string; title: string; body: string; action?: string; actionLink?: string } } = {
      'MAINTENANCE_REMINDER': {
        subject: 'Action Required: Scheduled Maintenance Reminder',
        title: 'Maintenance Check Required',
        body: `<p>Dear ${data.name},</p><p>This is a friendly reminder to schedule your routine maintenance check for your rented appliances. Regular maintenance ensures optimal performance and longevity.</p><p>Please contact our support team to arrange a convenient time.</p>`,
        action: 'Contact Support',
        actionLink: 'mailto:support@nestlysuite.com'
      },
      'CLEANUP_REMINDER': {
        subject: 'Maintenance Alert: 90-Day Cleanup Required',
        title: 'Quarterly Machine Cleanup',
        body: `<p>Dear ${data.name},</p><p>It is time for your quarterly machine cleanup. Please ensure your washer and dryer are cleaned according to the maintenance guidelines provided in your lease agreement.</p><p>Keeping your machines clean prevents issues and ensures they run efficiently.</p>`
      },
      'LEASE_EXPIRY': {
        subject: 'Important: Your Lease is Expiring Soon',
        title: 'Lease Expiration Notice',
        body: `<p>Dear ${data.name},</p><p>Your current lease agreement is set to expire soon. We value you as a customer and would love to discuss renewal options with you.</p><p>Please log in to your dashboard to view your renewal options.</p>`,
        action: 'View Renewal Options',
        actionLink: 'https://www.nestlysuite.com/#/dashboard'
      },
      'PHOTO_REPORT': {
        subject: 'Action Required: Submit 90-Day Photo Report',
        title: 'Quarterly Photo Report',
        body: `<p>Dear ${data.name},</p><p>As part of your rental agreement, please submit your 90-day machine photo report. This helps us verify the condition of the equipment.</p><p>You can easily upload photos through your customer dashboard.</p>`,
        action: 'Submit Report',
        actionLink: 'https://www.nestlysuite.com/#/dashboard'
      }
    };

    const message = reminderMessages[data.type] || { subject: 'Notification from Nestly Suite', title: 'Notification', body: 'You have a new notification from Nestly Suite.' };

    const htmlContent = backendService.generateEmailHtml(
      message.title,
      message.body,
      message.actionLink,
      message.action
    );

    // Send via EmailJS
    await backendService.triggerEmail(data.email, message.subject, htmlContent);

    // Log the message
    await addDoc(collection(db, "messages"), {
      customerId: data.customerId,
      type: data.type,
      subject: message.subject,
      body: message.body,
      sentAt: new Date().toISOString(),
      status: 'SENT'
    });
  },

  /**
   * Allows an Admin to create a Customer account without logging themselves out.
   * Uses a secondary Firebase App instance.
   */
  registerCustomerByAdmin: async (data: any, files?: { water: File | null, power: File | null, vent: File | null, residence: File | null, governmentId?: File | null }) => {
    // 1. Initialize a secondary app instance
    const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
    const secondaryAuth = getAuth(secondaryApp);

    try {
      // 2. Create the user in the secondary auth
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, data.email, data.password);
      const uid = userCredential.user.uid;

      // Upload files if they exist — use applications/{uid}/ path which Storage rules allow
      const folder = `applications/${uid}`;
      let waterUrl = "", powerUrl = "", ventUrl = "", residenceUrl = "", govIdUrl = "";
      if (files) {
        if (files.water) waterUrl = await uploadFile(files.water, folder);
        if (files.power) powerUrl = await uploadFile(files.power, folder);
        if (files.vent) ventUrl = await uploadFile(files.vent, folder);
        if (files.residence) residenceUrl = await uploadFile(files.residence, folder);
        if (files.governmentId) govIdUrl = await uploadFile(files.governmentId, folder);
      }

      // 3. Create the Application/Profile Record in Firestore (Main DB)
      const newApplication: Partial<Application> = {
        id: uid,
        userId: uid, // Required for Firestore rules to check document ownership
        applicantName: data.applicantName,
        email: data.email.toLowerCase().trim(),
        phone: data.phone,
        address: data.address,
        customerType: 'APPLIANCE',
        status: AppStatus.APPROVED, // Admin created users start as Approved
        submissionDate: new Date().toISOString(),
        riskScore: 0,
        riskAnalysis: "Admin Created Account",
        waterHookup: waterUrl,
        powerHookup: powerUrl,
        ventHookup: ventUrl,
        residenceProof: residenceUrl,
        governmentIdUrl: govIdUrl || undefined,
      } as any;

      await setDoc(doc(db, "applications", uid), newApplication);

      // Send Welcome Email
      const welcomeHtml = backendService.generateEmailHtml(
        'Welcome to Nestly Suite!',
        `<p>Dear ${data.applicantName},</p>
         <p>Your account has been successfully created by our team. We are excited to have you on board!</p>
         <p>You can now log in to your customer dashboard to view your application status and manage your account.</p>
         <p><strong>Username:</strong> ${data.email}</p>
         <p><strong>Password:</strong> ${data.password}</p>
         <p><em>Please change your password after your first login for security.</em></p>`,
        'https://www.nestlysuite.com/#/dashboard',
        'Login to Dashboard'
      );

      await backendService.triggerEmail(data.email, "Welcome to Nestly Suite - Account Created", welcomeHtml);

      // 5. Cleanup: Sign out of secondary auth and delete app to prevent conflicts
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      return newApplication;

    } catch (error) {
      // Cleanup if error
      await deleteApp(secondaryApp);
      throw error;
    }
  },

  /**
   * Onboards an existing synced customer (from Stripe) by creating an Auth user
   * and linking it to their existing application document.
   */
  onboardSyncedCustomer: async (appId: string, email: string, name: string) => {
    // 1. Generate a temporary password
    const tempPassword = Math.random().toString(36).slice(-8) + "Aa1!";

    // 2. Initialize secondary app
    const secondaryApp = initializeApp(firebaseConfig, "SecondaryAppOnboard");
    const secondaryAuth = getAuth(secondaryApp);

    try {
      // 3. Check if user already exists (try to sign in, if fails, create)
      // Actually simpler to just try create. If exists, we might need reset password flow.
      // For now assume they don't exist in Auth.

      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, tempPassword);
      const uid = userCredential.user.uid;

      // 4. Update the EXISTING application document with the new userId
      // We do NOT change the document ID, but we set the userId field which allows access via rules.
      await updateDoc(doc(db, "applications", appId), {
        userId: uid,
        updatedAt: new Date().toISOString()
      });

      // 5. Send Welcome Email
      const welcomeHtml = backendService.generateEmailHtml(
        'Welcome to Nestly Suite!',
        `<p>Dear ${name},</p>
         <p>Your existing rental account has been activated for online access.</p>
         <p>You can now log in to your customer dashboard to manage your subscription and view your lease.</p>
         <p><strong>Username:</strong> ${email}</p>
         <p><strong>Temporary Password:</strong> ${tempPassword}</p>
         <p><em>Please change your password after your first login.</em></p>`,
        'https://www.nestlysuite.com/#/dashboard',
        'Login to Dashboard'
      );

      await backendService.triggerEmail(email, "Account Activation - Nestly Suite", welcomeHtml);

      console.log(`Onboarded customer ${email} with uid ${uid}`);

      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      return { success: true, uid };

    } catch (error: any) {
      await deleteApp(secondaryApp);
      if (error.code === 'auth/email-already-in-use') {
        // Handle case where auth account exists but maybe not linked?
        // For now just throw friendly error
        throw new Error("This email is already registered. Please check if they already have an account.");
      }
      throw error;
    }
  },

  // --- Core Application Logic ---

  submitApplication: async (
    formData: any,
    password: string,
    files: { water: File, power: File, vent: File, residence: File, governmentId?: File }
  ) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
      const user = userCredential.user;
      if (!user) throw new Error("Failed to create user");

      const folder = `applications/${user.uid}`;
      const [waterUrl, powerUrl, ventUrl, residenceUrl, govIdUrl] = await Promise.all([
        uploadFile(files.water, folder),
        uploadFile(files.power, folder),
        uploadFile(files.vent, folder),
        uploadFile(files.residence, folder),
        files.governmentId ? uploadFile(files.governmentId, folder) : Promise.resolve(''),
      ]);

      const newApp: Partial<Application> = {
        ...formData,
        id: user.uid,
        userId: user.uid, // Required for Firestore rules to check document ownership
        email: formData.email.toLowerCase().trim(),
        customerType: 'APPLIANCE',
        status: AppStatus.SUBMITTED,
        submissionDate: new Date().toISOString(),
        waterHookup: waterUrl,
        powerHookup: powerUrl,
        ventHookup: ventUrl,
        residenceProof: residenceUrl,
        governmentIdUrl: govIdUrl || undefined,
        riskScore: 0,
        riskAnalysis: "Pending"
      } as any;

      await setDoc(doc(db, "applications", user.uid), newApp);

      await backendService.triggerEmail(
        formData.email,
        "Application Received - Nestly Suite",
        `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #7c3aed, #5b21b6); padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px;">NestlySuite</h1>
          </div>
          <div style="background: #f8fafc; padding: 30px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px;">
            <h2 style="color: #1e293b; margin-top: 0;">Hi ${formData.applicantName},</h2>
            <p style="color: #475569; font-size: 16px; line-height: 1.6;">
              Thank you for applying to rent appliances with Nestly Suite! We've received your application and it's now under review.
            </p>
            
            <div style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 20px 0;">
              <h3 style="color: #7c3aed; margin-top: 0; font-size: 16px;">📋 What Happens Next?</h3>
              <ol style="color: #475569; padding-left: 20px; margin: 0;">
                <li style="margin-bottom: 8px;"><strong>Review (1-2 business days)</strong> - Our team reviews your application</li>
                <li style="margin-bottom: 8px;"><strong>Approval & Lease</strong> - If approved, you'll receive your lease agreement to sign</li>
                <li style="margin-bottom: 8px;"><strong>Schedule Installation</strong> - Pick a date for delivery and setup</li>
                <li style="margin-bottom: 0;"><strong>Start Using!</strong> - We install and you're ready to go</li>
              </ol>
            </div>
            
            <p style="color: #475569; font-size: 16px;">
              Track your application status anytime:
            </p>
            <a href="https://www.nestlysuite.com/#/dashboard" style="display: inline-block; background: #7c3aed; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: bold;">
              View My Application →
            </a>
            
            <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 30px 0;">
            <p style="color: #94a3b8; font-size: 14px; margin-bottom: 0;">
              Questions? Reply to this email or visit <a href="https://www.nestlysuite.com" style="color: #7c3aed;">www.nestlysuite.com</a>
            </p>
          </div>
        </div>
      `
      );
      return { ...newApp } as Application;
    } catch (error) {
      console.error("Error submitting application:", error);
      throw error;
    }
  },

  login: async (email: string, password: string) => {
    const normalizedEmail = email.toLowerCase().trim();
    const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
    return userCredential.user;
  },

  logout: async () => {
    await signOut(auth);
  },

  getAllApplications: async (): Promise<Application[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, "applications"));
      const applications: Application[] = [];

      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();

        applications.push({
          id: docSnap.id,
          userId: data.userId || null, // Allow null for synced users who need onboarding
          ...data
        } as Application);
      }

      return applications.filter(app => !isExcludedCustomer(app.applicantName));
    } catch (e) {
      if (await handleAuthError(e)) return [];
      console.error("Error fetching applications:", e);
      return [];
    }
  },

  getApplicationById: async (id: string): Promise<Application | null> => {
    try {
      const docRef = doc(db, "applications", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;

      const data = docSnap.data();

      return { id: docSnap.id, userId: data.userId || null, ...data } as Application;
    } catch (e) {
      await handleAuthError(e);
      return null;
    }
  },

  updateApplication: async (appId: string, updates: Partial<Application>) => {
    const appRef = doc(db, "applications", appId);
    await updateDoc(appRef, updates);

    // 1. Lease Sent Email
    if (updates.status === AppStatus.LEASE_SENT) {
      const app = await backendService.getApplicationById(appId);
      if (app) {
        const html = backendService.generateEmailHtml(
          'Application Approved - Action Required',
          `<p>Dear ${app.applicantName},</p>
           <p>Great news! Your application has been approved.</p>
           <p>We've generated your lease agreement. Please log in to your dashboard to review and sign it digitally.</p>
           <p>Once signed, you'll be one step closer to your delivery.</p>`,
          'https://www.nestlysuite.com/#/dashboard',
          'Review Lease'
        );
        await backendService.triggerEmail(app.email, "Action Required: Your Application is Approved", html);
      }
    }

    // 2. Installation Complete (Appreciation) Email
    if (updates.status === 'INSTALLED') {
      const app = await backendService.getApplicationById(appId);
      if (app) {
        const html = backendService.generateEmailHtml(
          'Welcome to the Family!',
          `<p>Dear ${app.applicantName},</p>
                 <p>Your installation is complete! We are thrilled to have you as a Nestly Suite customer.</p>
                 <p><strong>Next Steps:</strong></p>
                 <ul>
                    <li>Your monthly payments are set up automatically.</li>
                    <li>If you need any maintenance, simply log in and submit a ticket.</li>
                 </ul>
                 <p>Thank you for trusting us with your home appliances.</p>`,
          'https://www.nestlysuite.com/#/dashboard',
          'Go to Dashboard'
        );
        await backendService.triggerEmail(app.email, "Welcome to Nestly Suite - Installation Complete", html);
      }
    }

    if (updates.status === AppStatus.LEASE_SIGNED) {
      const app = await backendService.getApplicationById(appId);
      if (app) {
        const html = backendService.generateEmailHtml(
          'Lease Signed - Next Steps',
          `<p>Dear ${app.applicantName},</p>
           <p>Thank you for signing your lease agreement!</p>
           <p><strong>Next Step:</strong> Please log in to your dashboard and upload a valid Government ID (driver's license, state ID, or passport). Once your ID is verified, you'll be able to set up your subscription payment and schedule your installation.</p>`,
          'https://www.nestlysuite.com/#/dashboard',
          'Upload Your ID'
        );
        await backendService.triggerEmail(app.email, "Action Required: Upload Your ID", html);
      }
    }
    // 4. Application Rejected
    if (updates.status === AppStatus.REJECTED) {
      const app = await backendService.getApplicationById(appId);
      if (app) {
        // @ts-ignore - rejectionReason might not be in interface but passed in update
        const reason = (updates as any).rejectionReason || "Application criteria not met.";

        const html = backendService.generateEmailHtml(
          'Application Update',
          `<p>Dear ${app.applicantName},</p>
           <p>Thank you for your interest in Nestly Suite.</p>
           <p>After careful review, we regret to inform you that we are unable to approve your application at this time.</p>
           <div style="background: #fef2f2; border-left: 4px solid #ef4444; padding: 15px; margin: 15px 0;">
              <p style="margin: 0; font-weight: bold; color: #991b1b;">Reason for Rejection:</p>
              <p style="margin: 5px 0 0 0; color: #7f1d1d;">${reason}</p>
           </div>
           <p>If you have questions or believe this decision was made in error, please reply to this email.</p>`,
          'https://www.nestlysuite.com',
          'Visit Website'
        );
        await backendService.triggerEmail(app.email, "Application Status Update", html);
      }
    }
  },

  // Enhanced update with audit logging for admin profile edits
  updateApplicationWithAudit: async (
    appId: string,
    updates: Partial<Application>,
    adminEmail: string,
    reason?: string
  ): Promise<{ success: boolean; changesLogged: number }> => {
    // 1. Get current application state
    const currentApp = await backendService.getApplicationById(appId);
    if (!currentApp) throw new Error('Application not found');

    // 2. Detect changed fields (excluding internal/system fields)
    const trackableFields = [
      'email', 'phone', 'address', 'status', 'equipmentSerialNumber',
      'pickupScheduledDate', 'terminationReason', 'installationDate',
      'apartmentLeaseStartDate', 'apartmentLeaseEndDate', 'dob', 'adminNotes',
      'idType', 'idExpirationDate', 'leaseStartDate', 'leaseEndDate'
    ];

    const changes: Array<{ field: string; oldValue: string; newValue: string }> = [];

    for (const field of trackableFields) {
      const oldVal = (currentApp as any)[field];
      const newVal = (updates as any)[field];

      // Only log if field is being updated and value actually changed
      if (newVal !== undefined && String(oldVal || '') !== String(newVal || '')) {
        changes.push({
          field,
          oldValue: oldVal ? String(oldVal) : '(empty)',
          newValue: newVal ? String(newVal) : '(empty)'
        });
      }
    }

    // 3. Perform the update
    const appRef = doc(db, "applications", appId);
    await updateDoc(appRef, {
      ...updates,
      lastModifiedAt: new Date().toISOString(),
      lastModifiedBy: adminEmail
    });

    // 4. Log changes to activityLog if there were any
    if (changes.length > 0) {
      await addDoc(collection(db, "activityLog"), {
        customerId: appId,
        type: 'PROFILE_UPDATE',
        timestamp: new Date().toISOString(),
        actor: adminEmail,
        details: {
          changes,
          reason: reason || 'Admin profile update',
          notes: `${changes.length} field(s) modified`
        }
      });
    }

    return { success: true, changesLogged: changes.length };
  },

  // Archive a document before replacing it
  archiveDocument: async (
    customerId: string,
    documentType: string,
    currentUrl: string,
    adminEmail: string
  ): Promise<string> => {
    // Add to documentHistory subcollection
    const docRef = await addDoc(
      collection(db, "applications", customerId, "documentHistory"),
      {
        customerId,
        documentType,
        url: currentUrl,
        uploadedAt: new Date().toISOString(), // Original upload time unknown, use archive time
        uploadedBy: 'previous_upload',
        replacedAt: new Date().toISOString(),
        replacedBy: adminEmail
      }
    );
    return docRef.id;
  },

  // Upload a new customer document with archiving
  uploadCustomerDocument: async (
    customerId: string,
    file: File,
    documentType: 'waterHookup' | 'powerHookup' | 'ventHookup' | 'residenceProof' | 'governmentIdUrl' | 'idPhoto' | 'installPhoto' | 'signedLeaseUpload',
    adminEmail: string
  ): Promise<{ success: boolean; newUrl: string }> => {
    // 1. Get current document URL if exists
    const currentApp = await backendService.getApplicationById(customerId);
    if (!currentApp) throw new Error('Customer not found');

    const currentUrl = (currentApp as any)[documentType];

    // 2. Archive old document if it exists
    if (currentUrl) {
      await backendService.archiveDocument(customerId, documentType, currentUrl, adminEmail);
    }

    // 3. Upload new file
    const newUrl = await uploadFile(file, `applications/${customerId}`);

    // 4. Update application with new document URL
    const appRef = doc(db, "applications", customerId);
    await updateDoc(appRef, {
      [documentType]: newUrl,
      lastModifiedAt: new Date().toISOString(),
      lastModifiedBy: adminEmail
    });

    // 5. Log to activity
    await addDoc(collection(db, "activityLog"), {
      customerId,
      type: 'DOCUMENT_UPLOADED',
      timestamp: new Date().toISOString(),
      actor: adminEmail,
      details: {
        documentType,
        previousVersion: currentUrl ? 'archived' : 'none',
        notes: `New ${documentType} uploaded by ${adminEmail}`
      }
    });

    return { success: true, newUrl };
  },

  // Get document history for a customer
  getDocumentHistory: async (customerId: string, documentType?: string): Promise<any[]> => {
    try {
      let q;
      if (documentType) {
        q = query(
          collection(db, "applications", customerId, "documentHistory"),
          where('documentType', '==', documentType),
          orderBy('replacedAt', 'desc')
        );
      } else {
        q = query(
          collection(db, "applications", customerId, "documentHistory"),
          orderBy('replacedAt', 'desc')
        );
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching document history:', error);
      return [];
    }
  },

  // --- Support Tickets ---
  createTicket: async (ticket: any) => {
    await addDoc(collection(db, "tickets"), ticket);
  },

  getAllTickets: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "tickets"));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Ticket));
    } catch (e) {
      if (await handleAuthError(e)) return [];
      console.error("Error fetching tickets:", e);
      return [];
    }
  },

  updateTicket: async (id: string, updates: Partial<Ticket>) => {
    await updateDoc(doc(db, "tickets", id), updates);
  },

  addTicketMessage: async (ticketId: string, message: { sender: 'ADMIN' | 'CUSTOMER', text: string, date: string }) => {
    const ticketRef = doc(db, "tickets", ticketId);
    const ticketSnap = await getDoc(ticketRef);
    if (ticketSnap.exists()) {
      const ticketData = ticketSnap.data();
      const messages = ticketData.messages || [];
      await updateDoc(ticketRef, {
        messages: [...messages, message],
        updatedAt: new Date().toISOString() // Track last update
      });
    }
  },

  // --- Condition Reports ---
  submitConditionReport: async (report: Omit<CustomerReport, 'id' | 'status'>) => {
    const newReport = {
      ...report,
      status: 'SUBMITTED',
      createdAt: new Date().toISOString()
    };
    return await addDoc(collection(db, "reports"), newReport);
  },

  getConditionReports: async (): Promise<CustomerReport[]> => {
    const q = query(collection(db, "reports"), orderBy("submittedAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as CustomerReport));
  },



  // --- Financials ---
  getTransactions: async (): Promise<Transaction[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, "transactions"));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Transaction))
        .filter(t => !isExcludedCustomer((t as any).customerName))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (e) {
      if (await handleAuthError(e)) return [];
      console.error("Error fetching transactions:", e);
      return [];
    }
  },

  // Get specific customer transactions for the dashboard
  getCustomerTransactions: async (applicantName: string, stripeCustomerId?: string): Promise<Transaction[]> => {
    if (!applicantName) return []; // Guard against undefined applicantName
    const allTx = await backendService.getTransactions();
    const searchName = applicantName.toLowerCase();
    return allTx.filter(t => {
      // Match by Stripe Customer ID if available (most reliable)
      if (stripeCustomerId && (t as any).stripeCustomerId === stripeCustomerId) {
        return true;
      }
      // Match by customerName field (from Stripe sync)
      if ((t as any).customerName &&
        (t as any).customerName.toLowerCase().includes(searchName)) {
        return true;
      }
      // Match by description containing customer name
      if (t.description && t.description.toLowerCase().includes(searchName)) {
        return true;
      }
      return false;
    });
  },

  addTransaction: async (transaction: Omit<Transaction, 'id'>): Promise<Transaction> => {
    const docRef = await addDoc(collection(db, "transactions"), transaction);
    return { id: docRef.id, ...transaction };
  },

  // --- Payment Processing ---
  processPayment: async (token: string, amount: number) => {
    // In a real app, you would call a Cloud Function here passing the token
    // await httpsCallable(functions, 'createStripeCharge')({ token, amount });

    // Simulating backend delay and success
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Record the transaction
    await backendService.addTransaction({
      date: new Date().toISOString(),
      description: 'Stripe Payment (Simulated)',
      amount: amount / 100, // Convert cents to dollars
      type: 'INCOME',
      category: 'RENTAL'
    } as any);

    return { success: true };
  },


  // --- Messages / Mail ---
  getMessages: async (email: string) => {
    // Queries the 'mail' collection where 'to' array contains the email
    const q = query(collection(db, "mail"), where("to", "array-contains", email), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  sendDirectMessage: async (email: string, subject: string, body: string, customerId?: string): Promise<void> => {
    // Convert plain text to HTML with proper formatting
    const htmlBody = backendService.generateEmailHtml(
      subject,
      `<p>${body.replace(/\n/g, '</p><p>')}</p>`,
      'https://www.nestlysuite.com/#/dashboard',
      'Go to Dashboard'
    );

    // Use triggerEmail which routes through SendGrid for tracking
    await backendService.triggerEmail(email, subject, htmlBody, customerId);
  },

  // --- Conversations / Two-Way Messaging ---

  // Create a new conversation thread
  createConversation: async (data: {
    customerId: string;
    customerEmail: string;
    customerName: string;
    subject: string;
    initialMessage: string;
    sender: 'ADMIN' | 'CUSTOMER';
    senderEmail?: string;
    ccAdmin?: boolean; // Send copy to admin
  }): Promise<string> => {
    const now = new Date().toISOString();
    const conversationRef = await addDoc(collection(db, "conversations"), {
      customerId: data.customerId,
      customerEmail: data.customerEmail.toLowerCase().trim(),
      customerName: data.customerName,
      subject: data.subject,
      status: 'OPEN',
      lastMessageAt: now,
      lastMessageBy: data.sender,
      unreadByAdmin: data.sender === 'CUSTOMER',
      unreadByCustomer: data.sender === 'ADMIN',
      createdAt: now,
      ccAdmin: data.ccAdmin || false, // Track if CC was enabled
      messages: [{
        id: `msg_${Date.now()}`,
        sender: data.sender,
        senderEmail: data.senderEmail || (data.sender === 'ADMIN' ? 'support@nestlysuite.com' : data.customerEmail),
        content: data.initialMessage,
        sentAt: now,
        readAt: null,
        source: 'PORTAL',
        emailSentAt: null, // Will be set when email is sent
        emailDelivered: false
      }]
    });

    // If admin initiated, send email notification to customer
    if (data.sender === 'ADMIN') {
      const htmlBody = backendService.generateEmailHtml(
        data.subject,
        `<p>${data.initialMessage.replace(/\n/g, '</p><p>')}</p>
         <p style="color: #64748b; font-size: 14px; margin-top: 20px;">
           Reply to this email or log in to your dashboard to respond.
         </p>`,
        'https://www.nestlysuite.com/#/dashboard',
        'View Message'
      );
      await backendService.triggerEmail(data.customerEmail, data.subject, htmlBody, data.customerId);

      // Send CC to admin if enabled
      if (data.ccAdmin) {
        const ccHtmlBody = backendService.generateEmailHtml(
          `[CC] ${data.subject}`,
          `<div style="background: #f0f9ff; border-left: 4px solid #0284c7; padding: 12px; margin-bottom: 16px;">
             <p style="margin: 0; font-size: 13px; color: #0369a1;"><strong>📋 Copy of message sent to:</strong> ${data.customerName} (${data.customerEmail})</p>
           </div>
           <p>${data.initialMessage.replace(/\n/g, '</p><p>')}</p>`,
          `https://www.nestlysuite.com/#/admin/messages`,
          'View in Messages'
        );
        await backendService.triggerEmail('support@nestlysuite.com', `[CC] ${data.subject}`, ccHtmlBody);
      }
    }

    return conversationRef.id;
  },

  // Add a message to an existing conversation
  addMessageToConversation: async (
    conversationId: string,
    message: string,
    sender: 'ADMIN' | 'CUSTOMER',
    senderEmail: string,
    source: 'PORTAL' | 'EMAIL' = 'PORTAL'
  ): Promise<void> => {
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      throw new Error('Conversation not found');
    }

    const conversationData = conversationSnap.data();
    const now = new Date().toISOString();
    const newMessage = {
      id: `msg_${Date.now()}`,
      sender,
      senderEmail,
      content: message,
      sentAt: now,
      readAt: null,
      source
    };

    const updatedMessages = [...(conversationData.messages || []), newMessage];

    await updateDoc(conversationRef, {
      messages: updatedMessages,
      lastMessageAt: now,
      lastMessageBy: sender,
      unreadByAdmin: sender === 'CUSTOMER' ? true : conversationData.unreadByAdmin,
      unreadByCustomer: sender === 'ADMIN' ? true : conversationData.unreadByCustomer,
      status: 'OPEN' // Re-open if was closed
    });

    // Send email notification
    if (sender === 'ADMIN') {
      const htmlBody = backendService.generateEmailHtml(
        `Re: ${conversationData.subject}`,
        `<p>${message.replace(/\n/g, '</p><p>')}</p>
         <p style="color: #64748b; font-size: 14px; margin-top: 20px;">
           Reply to this email or log in to your dashboard to respond.
         </p>`,
        'https://www.nestlysuite.com/#/dashboard',
        'View Conversation'
      );
      await backendService.triggerEmail(
        conversationData.customerEmail,
        `Re: ${conversationData.subject}`,
        htmlBody,
        conversationData.customerId
      );
    }
  },

  // Get all conversations (for admin) or customer-specific conversations
  getConversations: async (customerId?: string, status?: 'OPEN' | 'CLOSED' | 'ALL'): Promise<any[]> => {
    let q;
    if (customerId) {
      q = query(
        collection(db, "conversations"),
        where("customerId", "==", customerId),
        orderBy("lastMessageAt", "desc")
      );
    } else {
      q = query(
        collection(db, "conversations"),
        orderBy("lastMessageAt", "desc")
      );
    }

    const snapshot = await getDocs(q);
    let conversations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filter by status if not 'ALL'
    if (status && status !== 'ALL') {
      conversations = conversations.filter((c: any) => c.status === status);
    }

    return conversations;
  },

  // Get a single conversation by ID
  getConversationById: async (conversationId: string): Promise<any | null> => {
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) {
      return null;
    }

    return { id: conversationSnap.id, ...conversationSnap.data() };
  },

  // Mark conversation as read by admin or customer
  markConversationRead: async (conversationId: string, by: 'ADMIN' | 'CUSTOMER'): Promise<void> => {
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationSnap = await getDoc(conversationRef);

    if (!conversationSnap.exists()) return;

    const conversationData = conversationSnap.data();
    const now = new Date().toISOString();

    // Update unread status
    const updateData: any = {
      [by === 'ADMIN' ? 'unreadByAdmin' : 'unreadByCustomer']: false
    };

    // Mark individual messages as read
    const updatedMessages = conversationData.messages.map((msg: any) => {
      if (msg.sender !== by && !msg.readAt) {
        return { ...msg, readAt: now };
      }
      return msg;
    });

    updateData.messages = updatedMessages;
    await updateDoc(conversationRef, updateData);
  },

  // Close a conversation
  closeConversation: async (conversationId: string): Promise<void> => {
    const conversationRef = doc(db, "conversations", conversationId);
    await updateDoc(conversationRef, { status: 'CLOSED' });
  },

  // Get unread conversation count for admin
  getUnreadConversationCount: async (): Promise<number> => {
    const q = query(
      collection(db, "conversations"),
      where("unreadByAdmin", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  },

  // Get unread conversation count for a customer
  getCustomerUnreadCount: async (customerId: string): Promise<number> => {
    const q = query(
      collection(db, "conversations"),
      where("customerId", "==", customerId),
      where("unreadByCustomer", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  },

  // Find or create conversation for email reply handling
  findOrCreateConversationByEmail: async (
    email: string,
    subject: string,
    messageContent: string
  ): Promise<string> => {
    // First, find the customer
    const appsRef = collection(db, 'applications');
    const customerQuery = query(appsRef, where('email', '==', email.toLowerCase().trim()));
    const customerSnap = await getDocs(customerQuery);

    if (customerSnap.empty) {
      throw new Error('Customer not found for email: ' + email);
    }

    const customerDoc = customerSnap.docs[0];
    const customerId = customerDoc.id;
    const customerData = customerDoc.data();

    // Try to find an existing open conversation with similar subject
    const conversationsQuery = query(
      collection(db, "conversations"),
      where("customerId", "==", customerId),
      where("status", "==", "OPEN"),
      orderBy("lastMessageAt", "desc")
    );
    const conversationsSnap = await getDocs(conversationsQuery);

    // Clean subject for comparison (remove Re:, Fwd:, etc.)
    const cleanSubject = subject.replace(/^(Re:|Fwd:|FW:)\s*/gi, '').trim();

    // Look for matching conversation
    for (const convDoc of conversationsSnap.docs) {
      const convData = convDoc.data();
      const convSubject = convData.subject.replace(/^(Re:|Fwd:|FW:)\s*/gi, '').trim();
      if (convSubject.toLowerCase() === cleanSubject.toLowerCase()) {
        // Add to existing conversation
        await backendService.addMessageToConversation(
          convDoc.id,
          messageContent,
          'CUSTOMER',
          email,
          'EMAIL'
        );
        return convDoc.id;
      }
    }

    // If no match, create new conversation
    return await backendService.createConversation({
      customerId,
      customerEmail: email,
      customerName: customerData.applicantName || 'Customer',
      subject: cleanSubject || 'General Inquiry',
      initialMessage: messageContent,
      sender: 'CUSTOMER',
      senderEmail: email
    });
  },

  // --- Customer Reports ---
  getReports: async (customerId: string): Promise<any[]> => {
    const q = query(collection(db, "reports"), where("customerId", "==", customerId), orderBy("submittedAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  submitReport: async (data: { customerId: string; customerName: string; photoUrl: string; notes: string; type: string }): Promise<void> => {
    await addDoc(collection(db, "reports"), {
      ...data,
      submittedAt: new Date().toISOString()
    });
    // Update the customer's lastReportDate
    await updateDoc(doc(db, "applications", data.customerId), { lastReportDate: new Date().toISOString() });
  },

  // --- Admin Document Resend ---
  resendDocument: async (customerId: string, documentType: 'LEASE' | 'WELCOME' | 'PAYMENT_REMINDER' | 'CUSTOM', customSubject?: string, customBody?: string): Promise<void> => {
    const app = await backendService.getApplicationById(customerId);
    if (!app) throw new Error('Customer not found');

    const templates: Record<string, { subject: string; title: string; body: string; action: string }> = {
      LEASE: {
        subject: 'Action Required: Your Lease Agreement',
        title: 'Lease Agreement Resent',
        body: `<p>Dear ${app.applicantName},</p><p>Per your request, we are resending the link to your lease agreement. Please review and sign it at your earliest convenience to proceed with your order.</p>`,
        action: 'Sign Lease Agreement'
      },
      WELCOME: {
        subject: 'Welcome to Nestly Suite',
        title: 'Welcome Aboard!',
        body: `<p>Dear ${app.applicantName},</p><p>Welcome to Nestly Suite! We are delighted to have you as a customer. Your account is fully active and ready for use.</p>`,
        action: 'Access Dashboard'
      },
      PAYMENT_REMINDER: {
        subject: 'Action Required: Payment Method Update',
        title: 'Payment Method Update',
        body: `<p>Dear ${app.applicantName},</p><p>This is a reminder to update or verify your payment method to ensure uninterrupted service. Please log in to your dashboard to manage your billing details.</p>`,
        action: 'Update Payment Method'
      }
    };

    let emailSubject = '';
    let emailHtml = '';

    if (documentType === 'CUSTOM') {
      emailSubject = customSubject || 'Message from Nestly Suite';
      emailHtml = backendService.generateEmailHtml(
        customSubject || 'Message from Nestly Suite',
        `<p>Dear ${app.applicantName},</p><p>${customBody || ''}</p>`,
        'https://www.nestlysuite.com/#/dashboard',
        'Go to Dashboard'
      );
    } else {
      const template = templates[documentType];
      emailSubject = template.subject;
      emailHtml = backendService.generateEmailHtml(
        template.title,
        template.body,
        'https://www.nestlysuite.com/#/dashboard',
        template.action
      );
    }

    await backendService.triggerEmail(app.email, emailSubject, emailHtml);

    // Log the message
    await addDoc(collection(db, "messages"), {
      customerId: customerId,
      customerEmail: app.email,
      type: `RESEND_${documentType}`,
      subject: emailSubject,
      sentAt: new Date().toISOString()
    });
  },

  // Request Re-upload of Document
  requestReupload: async (customerId: string, documentName: string, notes?: string): Promise<void> => {
    const app = await backendService.getApplicationById(customerId);
    if (!app) throw new Error('Customer not found');

    const html = backendService.generateEmailHtml(
      "Action Required: Please Re-upload Document",
      `<p>Dear ${app.applicantName},</p>
       <p>We are reviewing your application but need you to re-upload the following document:</p>
       <div style="background: #fff1f2; color: #9f1239; padding: 15px; border-radius: 6px; margin: 20px 0; border: 1px solid #fecdd3;">
         <strong>${documentName}</strong>
         ${notes ? `<p style="margin-top: 5px; font-size: 14px;">Note: ${notes}</p>` : ''}
       </div>
       <p>Please log in to your dashboard and upload a clear photo of this item to proceed.</p>`,
      "https://www.nestlysuite.com/#/dashboard",
      "Go to Dashboard"
    );

    await backendService.triggerEmail(app.email, `Action Required: Re-upload ${documentName}`, html);

    // Log the interaction
    await addDoc(collection(db, "messages"), {
      customerId: customerId,
      customerEmail: app.email,
      type: 'REQUEST_REUPLOAD',
      subject: `Re-upload ${documentName}`,
      body: notes || `Requested re-upload of ${documentName}`,
      sentAt: new Date().toISOString()
    });
  },

  // --- Regenerate and Send Lease ---
  regenerateAndSendLease: async (customerId: string): Promise<void> => {
    const app = await backendService.getApplicationById(customerId);
    if (!app) throw new Error('Customer not found');

    // Generate date values
    const leaseStartDate = new Date();
    const leaseEndDate = new Date();
    leaseEndDate.setFullYear(leaseEndDate.getFullYear() + 1);
    const monthlyRent = (app as any).monthlyRent || 50.00;

    // Use centralized lease template (single source of truth)
    const leaseContent = generateApplianceLease({
      customerName: app.applicantName,
      customerEmail: app.email,
      customerPhone: app.phone || '',
      customerAddress: app.address,
      equipmentDescription: (app as any).equipmentDescription,
      equipmentSerialNumber: (app as any).equipmentSerialNumber,
      equipmentValue: (app as any).purchasePrice || 700,
      monthlyRent: monthlyRent,
      minimumTerm: (app as any).minimumTerm || 6,
      startDate: leaseStartDate,
      endDate: leaseEndDate
    });

    // Update application with lease content, dates, and status
    await updateDoc(doc(db, "applications", customerId), {
      status: AppStatus.LEASE_SENT,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      leaseContent: leaseContent
    });

    await backendService.triggerEmail(
      app.email,
      'Your Lease Agreement is Ready!',
      backendService.generateEmailHtml(
        "Lease Agreement Ready",
        `<p>Dear ${app.applicantName},</p>
         <p>Your lease agreement is ready for signing. Please log in to your dashboard to review and sign.</p>
         <div style="background: #f1f5f9; padding: 15px; border-radius: 6px; margin: 20px 0;">
            <p style="margin: 0; color: #475569;"><strong>Lease Period:</strong> ${leaseStartDate.toLocaleDateString()} - ${leaseEndDate.toLocaleDateString()}</p>
            <p style="margin: 10px 0 0 0; color: #475569;"><strong>Monthly Rent:</strong> $${monthlyRent.toFixed(2)}</p>
         </div>`,
        "https://www.nestlysuite.com/#/dashboard",
        "Sign Your Lease"
      )
    );
  },

  // --- Sync Stripe Customers ---
  syncStripeCustomers: async (): Promise<{ success: boolean, count: number, names: string[] }> => {
    try {
      const syncFn = httpsCallable(functions, 'syncStripeCustomers');
      const result = await syncFn();
      return result.data as { success: boolean, count: number, names: string[] };
    } catch (error) {
      console.error("Error syncing Stripe customers:", error);
      throw error;
    }
  },

  // Helper to generate consistent lease text - NOW USES CENTRALIZED TEMPLATE
  getLeaseTemplate: (data: {
    name: string;
    address: string;
    startDate: string | Date;
    endDate: string | Date;
    monthlyRent: number;
    email?: string;
    phone?: string;
    applianceType?: ApplianceType;
    equipmentDescription?: string;
    equipmentSerialNumber?: string;
    equipmentValue?: number;
    minimumTerm?: number;
  }): string => {
    const sDate = typeof data.startDate === 'string' ? new Date(data.startDate) : data.startDate;
    const eDate = typeof data.endDate === 'string' ? new Date(data.endDate) : data.endDate;

    // Use centralized appliance lease template (single source of truth)
    return generateApplianceLease({
      customerName: data.name,
      customerEmail: data.email || '',
      customerPhone: data.phone || '',
      customerAddress: data.address,
      applianceType: data.applianceType || 'washer_dryer',
      equipmentDescription: data.equipmentDescription,
      equipmentSerialNumber: data.equipmentSerialNumber,
      equipmentValue: data.equipmentValue,
      monthlyRent: data.monthlyRent,
      minimumTerm: data.minimumTerm || 6,
      startDate: sDate,
      endDate: eDate
    });
  },

  // Generate lease content only (for customer self-service or fallback)
  generateLeaseOnly: async (customerId: string): Promise<void> => {
    const app = await backendService.getApplicationById(customerId);
    if (!app) throw new Error('Customer not found');

    // Validate all required fields are present
    const missingFields: string[] = [];

    if (!app.applicantName || app.applicantName.trim() === '') {
      missingFields.push('Full Name');
    }
    if (!app.email || app.email.trim() === '') {
      missingFields.push('Email');
    }
    if (!app.phone || app.phone.trim() === '') {
      missingFields.push('Phone Number');
    }
    if (!app.address || app.address.trim() === '') {
      missingFields.push('Address');
    }
    if (!app.waterHookup) {
      missingFields.push('Water Hookup Photo');
    }
    if (!app.powerHookup) {
      missingFields.push('Power Hookup Photo');
    }
    if (!app.ventHookup) {
      missingFields.push('Dryer Vent Photo');
    }
    if (!app.residenceProof) {
      missingFields.push('Proof of Residence');
    }

    // If any fields are missing, throw an error with details
    if (missingFields.length > 0) {
      throw new Error(
        `Cannot generate lease: Missing required information - ${missingFields.join(', ')}. ` +
        `Please ensure the customer has completed their application with all required details.`
      );
    }

    // Generate date values
    const leaseStartDate = new Date();
    const leaseEndDate = new Date();
    leaseEndDate.setFullYear(leaseEndDate.getFullYear() + 1);
    const monthlyRent = 50.00;

    // Generate lease content using the helper
    const leaseContent = backendService.getLeaseTemplate({
      name: app.applicantName,
      address: app.address,
      startDate: leaseStartDate,
      endDate: leaseEndDate,
      monthlyRent: monthlyRent
    });

    // Update application with lease content, dates, and status
    // Note: We don't change status to LEASE_SENT here to avoid confusing the flow if they are already APPROVED
    // But if they are APPROVED, they should be able to sign. 
    // We will set status to LEASE_SENT to ensure consistency with the "Lease Sent" state.
    await updateDoc(doc(db, "applications", customerId), {
      status: AppStatus.LEASE_SENT,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      leaseContent: leaseContent
    });

    // Send lease notification email
    const emailHtml = backendService.generateEmailHtml(
      "Application Approved - Lease Ready",
      `< p > Dear ${app.applicantName},</p>
  < p > Congratulations! Your application has been approved.</p>
    < p > Your lease agreement is now ready for your review and signature.Please log in to your dashboard to complete this step and schedule your delivery.</p>
      < div style = "background: #f1f5f9; padding: 15px; border-radius: 6px; margin: 20px 0;" >
        <p style="margin: 0; color: #475569;" > <strong>Lease Period: </strong> ${leaseStartDate.toLocaleDateString()} - ${leaseEndDate.toLocaleDateString()}</p >
          <p style="margin: 10px 0 0 0; color: #475569;" > <strong>Monthly Rent: </strong> $${monthlyRent.toFixed(2)}</p >
            </div>`,
      "https://www.nestlysuite.com/#/dashboard",
      "Sign Your Lease"
    );

    await backendService.triggerEmail(app.email, "Action Required: Lease Agreement Ready", emailHtml);
  },

  // --- Quick Lease (No Account Required) ---

  // Preview only — returns the lease text and email HTML without saving or sending anything
  generateQuickLeasePreview: (data: {
    name: string;
    email: string;
    address: string;
    phone?: string;
    monthlyRent?: number;
    applianceType?: ApplianceType;
    durationMonths?: number;
  }): { leaseContent: string; emailHtml: string; signingLink: string } => {
    const durationMonths = data.durationMonths || 12;
    const leaseStartDate = new Date();
    const leaseEndDate = new Date();
    leaseEndDate.setMonth(leaseEndDate.getMonth() + durationMonths);
    const defaults = getDefaultEquipment(data.applianceType || 'washer_dryer');
    const monthlyRent = data.monthlyRent || defaults.rent;

    const leaseContent = backendService.getLeaseTemplate({
      name: data.name,
      address: data.address,
      email: data.email,
      phone: data.phone,
      startDate: leaseStartDate,
      endDate: leaseEndDate,
      monthlyRent: monthlyRent,
      applianceType: data.applianceType || 'washer_dryer',
      minimumTerm: durationMonths
    });

    const signingLink = 'https://www.nestlysuite.com/#/sign-lease?id=PREVIEW';

    const emailHtml = backendService.generateEmailHtml(
      'Your Lease Agreement from Nestly Suite',
      `<p>Dear ${data.name},</p>
       <p>Thank you for your interest in Nestly Suite's appliance rental service!</p>
       <p>Please review your personalized lease agreement below. When you're ready, click the button to sign digitally.</p>
       
       <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; margin: 20px 0; font-family: monospace; font-size: 12px; white-space: pre-wrap; line-height: 1.5;">
${leaseContent.replace(/\n/g, '<br>')}
       </div>`,
      signingLink,
      'Sign Lease Agreement'
    );

    return { leaseContent, emailHtml, signingLink };
  },

  // Generate a PDF of the lease for admin download/preview
  generateQuickLeasePDF: (leaseContent: string, customerName: string): void => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;
    const lineHeight = 5.5;
    const margin = 20;
    const textWidth = pageWidth - 2 * margin;

    // Helper function to add text with word wrap and auto page breaks
    const addText = (text: string, fontSize: number = 9, isBold: boolean = false) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text, textWidth);
      lines.forEach((line: string) => {
        if (y > 275) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, margin, y);
        y += lineHeight;
      });
    };

    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('NestlySuite', pageWidth / 2, y, { align: 'center' });
    y += 8;
    pdf.setFontSize(13);
    pdf.text('APPLIANCE RENTAL LEASE AGREEMENT', pageWidth / 2, y, { align: 'center' });
    y += 4;

    // Draw header line
    pdf.setDrawColor(30, 58, 95);
    pdf.setLineWidth(0.8);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 8;

    // Parse the lease content and render each line
    const contentLines = leaseContent.split('\n');
    for (const line of contentLines) {
      const trimmed = line.trim();

      // Skip the first "APPLIANCE RENTAL AGREEMENT" title since we added a header
      if (trimmed === 'APPLIANCE RENTAL AGREEMENT') continue;

      // Section dividers
      if (/^═+$/.test(trimmed)) {
        y += 2;
        pdf.setDrawColor(30, 58, 95);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 4;
        continue;
      }

      // Section headers (numbered)
      if (/^\d+\.\s[A-Z]/.test(trimmed)) {
        y += 2;
        addText(trimmed, 11, true);
        y += 2;
        continue;
      }

      // Sub-section headers (lettered)
      if (/^[a-h]\.\s[A-Z]+:/.test(trimmed)) {
        addText(trimmed, 9, true);
        continue;
      }

      // Bullet points
      if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
        addText('  ' + trimmed, 9);
        continue;
      }

      // Empty lines
      if (trimmed === '') {
        y += 3;
        continue;
      }

      // Regular text
      addText(trimmed, 9);
    }

    // Footer on last page
    y += 10;
    pdf.setDrawColor(30, 58, 95);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    pdf.text(`© ${new Date().getFullYear()} NestlySuite Inc. All rights reserved.`, pageWidth / 2, y, { align: 'center' });
    y += 4;
    pdf.text('support@nestlysuite.com | www.nestlysuite.com', pageWidth / 2, y, { align: 'center' });
    pdf.setTextColor(0, 0, 0); // Reset

    // Trigger browser download with explicit blob approach for reliability
    const safeName = customerName.replace(/[^a-zA-Z0-9]/g, '_');
    const fileName = `NestlySuite_Lease_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    // Cleanup after a delay
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  },

  // Save the quick lease to Firestore WITHOUT sending the email (admin can send later)
  saveQuickLeaseOnly: async (data: {
    name: string;
    email: string;
    address: string;
    phone?: string;
    monthlyRent?: number;
    applicationId?: string;
    applianceType?: ApplianceType;
    durationMonths?: number;
  }): Promise<string> => {
    const durationMonths = data.durationMonths || 12;
    const leaseStartDate = new Date();
    const leaseEndDate = new Date();
    leaseEndDate.setMonth(leaseEndDate.getMonth() + durationMonths);
    const defaults = getDefaultEquipment(data.applianceType || 'washer_dryer');
    const monthlyRent = data.monthlyRent || defaults.rent;

    const leaseContent = backendService.getLeaseTemplate({
      name: data.name,
      address: data.address,
      email: data.email,
      phone: data.phone,
      startDate: leaseStartDate,
      endDate: leaseEndDate,
      monthlyRent: monthlyRent,
      applianceType: data.applianceType || 'washer_dryer',
      minimumTerm: durationMonths
    });

    // Auto-create customer record if no applicationId provided
    let applicationId = data.applicationId || '';
    if (!applicationId) {
      const newApp = await addDoc(collection(db, "applications"), {
        applicantName: data.name,
        email: data.email.toLowerCase().trim(),
        phone: data.phone || '',
        address: data.address,
        customerType: 'APPLIANCE',
        status: 'APPROVED',
        submissionDate: new Date().toISOString(),
        riskScore: 0,
        riskAnalysis: 'Quick Lease - Auto Created',
        leaseContent: leaseContent,
        leaseStartDate: leaseStartDate.toISOString(),
        leaseEndDate: leaseEndDate.toISOString(),
        monthlyRent: monthlyRent,
        applianceType: data.applianceType || 'washer_dryer',
        createdVia: 'quickLease'
      });
      applicationId = newApp.id;
    }

    const leaseDoc = await addDoc(collection(db, "quickLeases"), {
      recipientName: data.name,
      recipientEmail: data.email,
      recipientAddress: data.address,
      recipientPhone: data.phone || null,
      applianceType: data.applianceType || 'washer_dryer',
      monthlyRent: monthlyRent,
      leaseContent: leaseContent,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      savedAt: new Date().toISOString(),
      sentAt: null,
      signedAt: null,
      signature: null,
      applicationId: applicationId
    });

    // Update the application with the quick lease ID
    const appRef = doc(db, "applications", applicationId);
    await updateDoc(appRef, {
      leaseContent: leaseContent,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      monthlyRent: monthlyRent,
      quickLeaseId: leaseDoc.id
    });

    return leaseDoc.id;
  },

  // Full flow: save the lease AND send the email
  sendQuickLease: async (data: {
    name: string;
    email: string;
    address: string;
    phone?: string;
    monthlyRent?: number;
    applicationId?: string;
    applianceType?: ApplianceType;
    durationMonths?: number;
  }): Promise<void> => {
    const durationMonths = data.durationMonths || 12;
    const leaseStartDate = new Date();
    const leaseEndDate = new Date();
    leaseEndDate.setMonth(leaseEndDate.getMonth() + durationMonths);
    const defaults = getDefaultEquipment(data.applianceType || 'washer_dryer');
    const monthlyRent = data.monthlyRent || defaults.rent;

    const leaseContent = backendService.getLeaseTemplate({
      name: data.name,
      address: data.address,
      email: data.email,
      phone: data.phone,
      startDate: leaseStartDate,
      endDate: leaseEndDate,
      monthlyRent: monthlyRent,
      applianceType: data.applianceType || 'washer_dryer',
      minimumTerm: durationMonths
    });

    // Auto-create customer record if no applicationId provided
    let applicationId = data.applicationId || '';
    if (!applicationId) {
      const newApp = await addDoc(collection(db, "applications"), {
        applicantName: data.name,
        email: data.email.toLowerCase().trim(),
        phone: data.phone || '',
        address: data.address,
        customerType: 'APPLIANCE',
        status: 'LEASE_SENT',
        submissionDate: new Date().toISOString(),
        riskScore: 0,
        riskAnalysis: 'Quick Lease - Auto Created',
        leaseContent: leaseContent,
        leaseStartDate: leaseStartDate.toISOString(),
        leaseEndDate: leaseEndDate.toISOString(),
        monthlyRent: monthlyRent,
        applianceType: data.applianceType || 'washer_dryer',
        createdVia: 'quickLease'
      });
      applicationId = newApp.id;
    }

    // Store the quick lease in Firestore for signing
    const leaseDoc = await addDoc(collection(db, "quickLeases"), {
      recipientName: data.name,
      recipientEmail: data.email,
      recipientAddress: data.address,
      recipientPhone: data.phone || null,
      applianceType: data.applianceType || 'washer_dryer',
      monthlyRent: monthlyRent,
      leaseContent: leaseContent,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      sentAt: new Date().toISOString(),
      signedAt: null,
      signature: null,
      applicationId: applicationId
    });

    // Update the application with the quick lease ID
    const appRef = doc(db, "applications", applicationId);
    await updateDoc(appRef, {
      leaseContent: leaseContent,
      leaseStartDate: leaseStartDate.toISOString(),
      leaseEndDate: leaseEndDate.toISOString(),
      monthlyRent: monthlyRent,
      quickLeaseId: leaseDoc.id,
      status: 'LEASE_SENT'
    });

    // Generate signing link
    const signingLink = `https://www.nestlysuite.com/#/sign-lease?id=${leaseDoc.id}`;

    // Generate email HTML with signing link
    const emailHtml = backendService.generateEmailHtml(
      'Your Lease Agreement from Nestly Suite',
      `<p>Dear ${data.name},</p>
       <p>Thank you for your interest in Nestly Suite's appliance rental service!</p>
       <p>Please review your personalized lease agreement below. When you're ready, click the button to sign digitally.</p>
       
       <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; margin: 20px 0; font-family: monospace; font-size: 12px; white-space: pre-wrap; line-height: 1.5;">
${leaseContent.replace(/\n/g, '<br>')}
       </div>`,
      signingLink,
      'Sign Lease Agreement'
    );

    await backendService.triggerEmail(
      data.email,
      'Your Lease Agreement - Nestly Suite (Action Required)',
      emailHtml
    );

    // Non-blocking: Log for tracking (don't let this fail the core flow)
    addDoc(collection(db, "broadcasts"), {
      type: 'QUICK_LEASE',
      leaseId: leaseDoc.id,
      recipientName: data.name,
      recipientEmail: data.email,
      recipientAddress: data.address,
      applianceType: data.applianceType || 'washer_dryer',
      monthlyRent: monthlyRent,
      applicationId: data.applicationId || null,
      sentAt: new Date().toISOString()
    }).catch(err => console.warn('Broadcast log write failed (non-critical):', err.message));
  },

  // --- Quick Lease Admin Functions ---

  // Fetch ALL quick leases (regardless of status) for management
  getAllQuickLeases: async (): Promise<any[]> => {
    const snapshot = await getDocs(
      query(collection(db, "quickLeases"), orderBy("savedAt", "desc"))
    );
    return snapshot.docs.map(d => ({
      id: d.id,
      source: 'quickLease',
      ...d.data()
    }));
  },

  // --- Signed Leases Admin Functions ---

  getSignedQuickLeases: async (): Promise<any[]> => {
    const q = query(
      collection(db, "quickLeases"),
      where("signedAt", "!=", null),
      orderBy("signedAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      source: 'quickLease', // Mark the source for UI differentiation
      ...doc.data()
    }));
  },

  // Fetch application-based leases that customers have signed (status = LEASE_SIGNED)
  // and optionally filter those not yet admin-acknowledged
  getSignedApplicationLeases: async (): Promise<any[]> => {
    const q = query(
      collection(db, "applications"),
      where("status", "==", "LEASE_SIGNED")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        source: 'application', // Mark the source for UI differentiation
        recipientName: data.applicantName,
        recipientEmail: data.email,
        recipientAddress: data.address,
        signedAt: data.leaseSignedDate || data.leaseSigned || data.updatedAt,
        adminSignedAt: data.adminAcknowledgedAt || null,
        leaseContent: data.leaseContent,
        monthlyRent: data.monthlyRent || 50,
        ...data
      };
    });
  },

  // Combined function to get ALL signed leases from both sources
  getAllSignedLeases: async (): Promise<any[]> => {
    const [quickLeases, appLeases] = await Promise.all([
      backendService.getSignedQuickLeases(),
      backendService.getSignedApplicationLeases()
    ]);

    // Combine and sort by signed date (most recent first)
    const allLeases = [...quickLeases, ...appLeases];
    return allLeases.sort((a, b) => {
      const dateA = new Date(a.signedAt || 0).getTime();
      const dateB = new Date(b.signedAt || 0).getTime();
      return dateB - dateA;
    });
  },

  // Acknowledge an application-based lease (admin marks it as reviewed/counter-signed)
  acknowledgeApplicationLease: async (appId: string, adminSignature: string): Promise<void> => {
    const adminAcknowledgedAt = new Date().toISOString();

    // Fetch the application first to get details for Stripe customer creation
    const app = await backendService.getApplicationById(appId);
    if (!app) {
      throw new Error('Application not found');
    }

    // Keep status as LEASE_SIGNED - it will change to SCHEDULED when admin schedules installation
    await updateDoc(doc(db, "applications", appId), {
      adminAcknowledgedAt: adminAcknowledgedAt,
      adminSignature: adminSignature
      // NOT changing status here - status stays LEASE_SIGNED until installation is scheduled
    });

    // Create Stripe customer record
    try {
      const createStripeCustomer = httpsCallable(functions, 'createStripeCustomerForLease');
      await createStripeCustomer({
        applicationId: appId,
        email: app.email,
        name: app.applicantName,
        address: app.address,
        phone: app.phone
      });
    } catch (stripeError: any) {
      console.error('Failed to create Stripe customer:', stripeError);
      // Don't block the acknowledgment if Stripe fails - log it but continue
    }

    // Send confirmation email
    const html = backendService.generateEmailHtml(
      'Lease Counter-Signed - Ready for Installation Scheduling',
      `<p>Dear ${app.applicantName},</p>
       <p>Great news! Your lease has been counter-signed by our team and is now fully executed.</p>
       <p><strong>Next Step:</strong> We will be in touch shortly to schedule your appliance delivery and installation.</p>
       <p>In the meantime, please ensure you have set up your payment method in your dashboard.</p>`,
      'https://www.nestlysuite.com/#/dashboard',
      'Go to Dashboard'
    );
    await backendService.triggerEmail(app.email, "Your Lease is Complete - NestlySuite", html);
  },

  updateQuickLease: async (leaseId: string, data: any): Promise<void> => {
    await updateDoc(doc(db, "quickLeases", leaseId), data);
  },

  deleteQuickLease: async (leaseId: string): Promise<void> => {
    await deleteDoc(doc(db, "quickLeases", leaseId));
  },

  adminSignLease: async (leaseId: string, adminSignature: string): Promise<void> => {
    const adminSignedAt = new Date().toISOString();

    // Update lease with admin signature
    await updateDoc(doc(db, "quickLeases", leaseId), {
      adminSignature: adminSignature,
      adminSignedAt: adminSignedAt
    });

    // Fetch the full lease to send final copy
    const leaseDoc = await getDoc(doc(db, "quickLeases", leaseId));
    if (!leaseDoc.exists()) {
      throw new Error("Lease not found");
    }
    const lease = leaseDoc.data();

    // Generate PDF of signed lease
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;
    const lineHeight = 6;
    const margin = 20;
    const textWidth = pageWidth - 2 * margin;

    // Helper function to add text with word wrap
    const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text, textWidth);
      lines.forEach((line: string) => {
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, margin, y);
        y += lineHeight;
      });
    };

    // Title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('NestlySuite', pageWidth / 2, y, { align: 'center' });
    y += 8;
    pdf.setFontSize(14);
    pdf.text('APPLIANCE RENTAL LEASE AGREEMENT', pageWidth / 2, y, { align: 'center' });
    y += 12;

    // Agreement Info
    addText(`Agreement Date: ${new Date(lease.leaseStartDate).toLocaleDateString()}`, 10);
    addText('Contact: support@nestlysuite.com', 10);
    y += 5;

    // Parties Section
    addText('PARTIES', 12, true);
    addText(`Lessor: Nestly Suite Inc`);
    addText(`Lessee: ${lease.recipientName}`);
    addText(`Email: ${lease.recipientEmail}`);
    addText(`Phone: ${lease.recipientPhone || 'Not provided'}`);
    addText(`Address: ${lease.recipientAddress}`);
    y += 5;

    // Rented Appliances
    addText('RENTED APPLIANCES', 12, true);
    addText(`GE Dryer/Amana Washer - Model: GUD27 - Monthly Rent: $${lease.monthlyRent?.toFixed(2) || '40.00'}`);
    y += 5;

    // Lease Term
    addText('LEASE TERM', 12, true);
    addText(`Start Date: ${new Date(lease.leaseStartDate).toLocaleDateString()}`);
    addText('Term: 6-Month Fixed Term (Auto-Renews)');
    addText(`Est. End Date: ${new Date(lease.leaseEndDate).toLocaleDateString()}`);
    y += 5;

    // Rental Payments
    addText('RENTAL PAYMENTS', 12, true);
    addText(`Monthly Rent: $${lease.monthlyRent?.toFixed(2) || '40.00'} due 1st of each month`);
    addText('Late Fee: $10.00 (after 5 days)');
    addText('Early Termination Fee: $100.00');
    y += 5;

    // Signatures Section
    addText('SIGNATURES', 12, true);
    y += 3;
    addText('LESSEE (CUSTOMER):', 10, true);
    pdf.setFont('times', 'italic');
    pdf.setFontSize(16);
    pdf.text(lease.signature || '', margin, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Signed on: ${new Date(lease.signedAt).toLocaleString()}`, margin, y);
    y += 10;

    addText('LESSOR (NESTLY SUITE INC):', 10, true);
    pdf.setFont('times', 'italic');
    pdf.setFontSize(16);
    pdf.text(adminSignature, margin, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Signed on: ${new Date(adminSignedAt).toLocaleString()}`, margin, y);
    y += 15;

    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(100);
    pdf.text('Generated by Nestly Suite Management System. Nestly Suite Inc, a subsidiary of RedManna Inc.', pageWidth / 2, 285, { align: 'center' });

    // Convert PDF to blob and upload to Firebase Storage
    const pdfBlob = pdf.output('blob');
    const fileName = `leases/${leaseId}_signed_${Date.now()}.pdf`;
    const storageRef = ref(storage, fileName);
    await uploadBytes(storageRef, pdfBlob);
    const pdfUrl = await getDownloadURL(storageRef);

    // Update lease with PDF URL
    await updateDoc(doc(db, "quickLeases", leaseId), {
      signedPdfUrl: pdfUrl
    });

    // Generate final copy email with both signatures and PDF link
    const finalCopyHtml = `
      <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
        <div style="background: #10b981; color: white; padding: 20px; text-align: center;">
          <h2 style="margin: 0;">✅ Lease Agreement Fully Executed</h2>
        </div>
        <div style="padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0;">
          <p>Dear ${lease.recipientName},</p>
          <p>Your lease agreement has been fully executed with both signatures. Please keep this email and the attached PDF for your records.</p>
          
          <div style="background: #10b981; padding: 15px; border-radius: 8px; text-align: center; margin: 20px 0;">
            <a href="${pdfUrl}" style="color: white; font-weight: bold; text-decoration: none; font-size: 16px;">
              📄 Download Your Signed Lease (PDF)
            </a>
          </div>
          
          <div style="background: white; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #1e293b;">Lease Summary</h3>
            <p><strong>Property Address:</strong> ${lease.recipientAddress}</p>
            <p><strong>Monthly Rent:</strong> $${lease.monthlyRent?.toFixed(2) || '40.00'}</p>
            <p><strong>Lease Start:</strong> ${new Date(lease.leaseStartDate).toLocaleDateString()}</p>
            <p><strong>Minimum Term:</strong> 6 months</p>
            
            <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
            
            <h4 style="color: #1e293b;">Signatures</h4>
            <p><strong>Customer (${lease.recipientName}):</strong></p>
            <p style="font-family: 'Brush Script MT', cursive; font-size: 24px; margin: 5px 0;">${lease.signature}</p>
            <p style="font-size: 12px; color: #64748b;">Signed on: ${new Date(lease.signedAt).toLocaleString()}</p>
            
            <p style="margin-top: 20px;"><strong>Nestly Suite Inc:</strong></p>
            <p style="font-family: 'Brush Script MT', cursive; font-size: 24px; margin: 5px 0;">${adminSignature}</p>
            <p style="font-size: 12px; color: #64748b;">Signed on: ${new Date(adminSignedAt).toLocaleString()}</p>
          </div>
          
          <p>Our team will be in touch shortly to schedule your appliance delivery and installation.</p>
          <p>If you have any questions, please contact us at <a href="mailto:support@nestlysuite.com">support@nestlysuite.com</a>.</p>
          
          <p style="margin-top: 20px;">Thank you for choosing Nestly Suite!</p>
        </div>
        <div style="padding: 15px; background: #f1f5f9; text-align: center; font-size: 12px; color: #64748b;">
          Generated by Nestly Suite Management System. Nestly Suite Inc, a subsidiary of RedManna Inc.
        </div>
      </div>
    `;

    // Send final copy to customer
    await backendService.triggerEmail(
      lease.recipientEmail,
      'Your Fully Executed Lease Agreement - Nestly Suite',
      finalCopyHtml
    );

    // Also notify admin
    await backendService.triggerEmail(
      'admin@nestlysuite.com',
      `Lease Fully Executed: ${lease.recipientName}`,
      `<p>The lease for <strong>${lease.recipientName}</strong> (${lease.recipientEmail}) has been fully executed.</p>
       <p><strong>Address:</strong> ${lease.recipientAddress}</p>
       <p>Customer signed: ${new Date(lease.signedAt).toLocaleString()}</p>
       <p>Admin signed: ${new Date(adminSignedAt).toLocaleString()}</p>
       <p><a href="${pdfUrl}">Download Signed PDF</a></p>`
    );
  },

  // ==========================================
  // INVENTORY MANAGEMENT
  // ==========================================

  getInventory: async (): Promise<InventoryItem[]> => {
    try {
      const q = query(collection(db, 'inventory'), orderBy('type'), orderBy('serialNumber'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryItem));
    } catch (e) {
      if (await handleAuthError(e)) return [];
      console.error("Error fetching inventory:", e);
      return [];
    }
  },

  addInventoryItem: async (item: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<InventoryItem> => {
    const newItem = {
      ...item,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, 'inventory'), newItem);
    return { id: docRef.id, ...newItem } as InventoryItem;
  },

  updateInventoryItem: async (id: string, updates: Partial<InventoryItem>) => {
    const itemRef = doc(db, 'inventory', id);
    return await updateDoc(itemRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  },

  deleteInventoryItem: async (id: string) => {
    return await deleteDoc(doc(db, 'inventory', id));
  },

  // ==========================================
  // INVENTORY-SCHEDULER INTEGRATION (v3.0)
  // ==========================================

  // Get available inventory (IN_STOCK and not reserved)
  getAvailableInventory: async (type?: 'WASHER' | 'DRYER' | 'STACKED_UNIT'): Promise<InventoryItem[]> => {
    try {
      const allInventory = await backendService.getInventory();
      return allInventory.filter(item => {
        const isAvailable = item.status === 'IN_STOCK' && !item.reservedForEventId;
        if (type) {
          return isAvailable && item.type === type;
        }
        return isAvailable;
      });
    } catch (e) {
      console.error("Error fetching available inventory:", e);
      return [];
    }
  },

  // Reserve an inventory item for a scheduled event
  reserveInventoryForEvent: async (
    inventoryId: string,
    eventId: string,
    customerId: string
  ): Promise<boolean> => {
    try {
      const itemRef = doc(db, 'inventory', inventoryId);
      const itemSnap = await getDoc(itemRef);

      if (!itemSnap.exists()) {
        console.error("Inventory item not found:", inventoryId);
        return false;
      }

      const item = itemSnap.data() as InventoryItem;
      if (item.status !== 'IN_STOCK' || item.reservedForEventId) {
        console.error("Item not available for reservation:", inventoryId);
        return false;
      }

      await updateDoc(itemRef, {
        reservedForEventId: eventId,
        reservedForCustomerId: customerId,
        reservedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      return true;
    } catch (e) {
      console.error("Error reserving inventory:", e);
      return false;
    }
  },

  // Release a reservation (if event cancelled or rescheduled)
  releaseInventoryReservation: async (eventId: string): Promise<boolean> => {
    try {
      const inventory = await backendService.getInventory();
      const reservedItem = inventory.find(item => item.reservedForEventId === eventId);

      if (reservedItem) {
        const itemRef = doc(db, 'inventory', reservedItem.id);
        await updateDoc(itemRef, {
          reservedForEventId: null,
          reservedForCustomerId: null,
          reservedAt: null,
          updatedAt: new Date().toISOString()
        });
      }

      return true;
    } catch (e) {
      console.error("Error releasing inventory reservation:", e);
      return false;
    }
  },

  // Mark inventory as rented (after installation completes)
  markInventoryAsRented: async (
    inventoryId: string,
    customerId: string,
    customerAddress: string
  ): Promise<boolean> => {
    try {
      const itemRef = doc(db, 'inventory', inventoryId);
      await updateDoc(itemRef, {
        status: 'RENTED',
        assignedToCustomerId: customerId,
        currentLocation: customerAddress,
        reservedForEventId: null,
        reservedAt: null,
        rentedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      return true;
    } catch (e) {
      console.error("Error marking inventory as rented:", e);
      return false;
    }
  },


  // ==========================================
  // PRESALE MANAGEMENT
  // ==========================================

  getPresaleOrder: async (orderId: string) => {
    try {
      const docRef = doc(db, 'presale_orders', orderId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (e) {
      console.error("Error fetching presale order:", e);
      return null;
    }
  },

  getPresaleOrders: async () => {
    try {
      const snapshot = await getDocs(collection(db, 'presale_orders'));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error("Error fetching presale orders:", e);
      return [];
    }
  },

  getWaitlist: async () => {
    try {
      const q = query(collection(db, 'waitlist'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error("Error fetching waitlist:", e);
      return [];
    }
  },

  sendPresaleApplicationLink: async (orderId: string) => {
    try {
      const sendLink = httpsCallable(functions, 'sendPresaleApplicationLink');
      const result = await sendLink({ presaleOrderId: orderId });
      return result.data;
    } catch (e) {
      console.error("Error sending presale application link:", e);
      throw e;
    }
  },

  submitPresaleApplication: async (
    formData: any,
    password: string,
    files: { water: File, power: File, vent: File, residence: File },
    presaleOrderId: string
  ) => {
    try {
      // Get presale order to calculate credits
      const order = await backendService.getPresaleOrder(presaleOrderId) as any;
      if (!order) throw new Error("Presale order not found");

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
      const user = userCredential.user;
      if (!user) throw new Error("Failed to create user");

      // Upload files
      const folder = `applications/${user.uid}`;
      const [waterUrl, powerUrl, ventUrl, residenceUrl] = await Promise.all([
        uploadFile(files.water, folder),
        uploadFile(files.power, folder),
        uploadFile(files.vent, folder),
        uploadFile(files.residence, folder),
      ]);

      // Calculate presale credits
      let presaleCredits = {};
      if (order.type === 'early_access') {
        // $99 deposit gives $100 credit (first 2 months free at $40/month)
        presaleCredits = {
          depositAmount: 99,
          totalCredit: 100,
          monthsCovered: 2
        };
      } else if (order.type === 'subscription_advance') {
        presaleCredits = {
          prepaidMonths: 12,
          prepaidAmount: 499
        };
      }

      // Create application with presale metadata
      const newApp: Partial<Application> = {
        ...formData,
        id: user.uid,
        userId: user.uid,
        email: formData.email.toLowerCase().trim(),
        status: AppStatus.SUBMITTED,
        submissionDate: new Date().toISOString(),
        waterHookup: waterUrl,
        powerHookup: powerUrl,
        ventHookup: ventUrl,
        residenceProof: residenceUrl,
        riskScore: 0,
        riskAnalysis: "Pending",
        presaleSource: order.type,
        presaleOrderId: presaleOrderId,
        presaleCredits: presaleCredits,
        creditsApplied: false
      };

      await setDoc(doc(db, "applications", user.uid), newApp);

      // Update presale order status
      await updateDoc(doc(db, 'presale_orders', presaleOrderId), {
        status: 'converted',
        convertedAt: new Date().toISOString(),
        applicationId: user.uid
      });

      // Send confirmation email
      await backendService.triggerEmail(
        formData.email,
        "Application Received - Nestly Suite",
        backendService.generateEmailHtml(
          'Application Received',
          `<p>Hi ${formData.applicantName},</p>
           <p>Thank you for applying! We've received your application and it's now under review.</p>
           ${order.type === 'early_access' ? `
             <div style="background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 15px 0;">
               <p style="margin: 0; font-weight: bold; color: #065f46;">Your Presale Credits:</p>
               <p style="margin: 5px 0 0 0; color: #047857;">$99 deposit + $70 discount = $169 total credit</p>
             </div>
           ` : `
             <div style="background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 15px 0;">
               <p style="margin: 0; font-weight: bold; color: #065f46;">Your Subscription Advance:</p>
               <p style="margin: 5px 0 0 0; color: #047857;">12 months prepaid - No payments due for 1 year!</p>
             </div>
           `}
           <p>We'll review your application within 1-2 business days.</p>`,
          'https://www.nestlysuite.com/#/dashboard',
          'View Application'
        )
      );

      return { ...newApp } as Application;
    } catch (error) {
      console.error("Error submitting presale application:", error);
      throw error;
    }
  },

  // --- Legal Compliance: Activity Logging ---

  // Get activity log entries for a customer
  getCustomerActivityLog: async (customerId: string): Promise<any[]> => {
    try {
      // 1. Fetch from customer's subcollection (primary source for legal notices)
      const subcollectionRef = collection(db, 'applications', customerId, 'activityLog');
      const subcollectionQuery = query(subcollectionRef, orderBy('timestamp', 'desc'));
      let subcollectionActivities: any[] = [];
      try {
        const subcollectionSnapshot = await getDocs(subcollectionQuery);
        subcollectionActivities = subcollectionSnapshot.docs.map(d => ({ id: d.id, ...d.data(), source: 'subcollection' }));
      } catch (e) {
        console.log('No subcollection activity log found');
      }

      // 2. Fetch from global activityLog collection (legacy)
      let globalActivities: any[] = [];
      try {
        const activityQuery = query(
          collection(db, 'activityLog'),
          where('customerId', '==', customerId),
          orderBy('timestamp', 'desc')
        );
        const activitySnapshot = await getDocs(activityQuery);
        globalActivities = activitySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), source: 'global' }));
      } catch (e) {
        console.log('Could not fetch global activity log:', e);
      }

      // 3. Also try to get the customer email to fetch their emails from mail collection
      let customerEmail = '';
      try {
        const appDoc = await getDoc(doc(db, 'applications', customerId));
        if (appDoc.exists()) {
          customerEmail = appDoc.data().email;
        }
      } catch (e) {
        console.log('Could not fetch customer email for activity log');
      }

      // 4. Fetch emails sent to this customer from mail collection
      let mailActivities: any[] = [];
      if (customerEmail) {
        try {
          const mailSnapshot = await getDocs(collection(db, 'mail'));
          mailActivities = mailSnapshot.docs
            .filter(d => {
              const data = d.data();
              const toList = data.to || [];
              return toList.includes(customerEmail) ||
                (typeof toList === 'string' && toList === customerEmail);
            })
            .map(d => {
              const data = d.data();
              return {
                id: d.id,
                customerId: customerId,
                type: 'EMAIL_SENT',
                timestamp: data.createdAt || data.delivery?.startTime || new Date().toISOString(),
                details: {
                  subject: data.message?.subject || 'Email',
                  to: customerEmail,
                  deliveryStatus: (data.delivery?.state || 'sent').toLowerCase(),
                  deliveredAt: data.delivery?.endTime || null,
                  error: data.delivery?.error || null,
                  htmlContent: data.message?.html || null,
                  textContent: data.message?.text || null
                },
                actor: 'system',
                source: 'mail'
              };
            });
        } catch (e) {
          console.log('Could not fetch mail collection:', e);
        }
      }

      // 5. Combine all sources
      const combined: any[] = [...subcollectionActivities, ...globalActivities, ...mailActivities];

      // 6. Deduplicate by subject + timestamp (within same minute)
      // Prefer entries that have htmlContent
      const bestEntries = new Map<string, any>();
      for (const entry of combined) {
        const subject = entry.details?.subject || '';
        const timestamp = entry.timestamp ? new Date(entry.timestamp).toISOString().slice(0, 16) : '';
        const key = `${subject}_${timestamp}`;

        const existing = bestEntries.get(key);
        if (!existing) {
          bestEntries.set(key, entry);
        } else {
          // Prefer the entry with content
          const existingHasContent = existing.details?.htmlContent || existing.details?.textContent;
          const newHasContent = entry.details?.htmlContent || entry.details?.textContent;
          if (newHasContent && !existingHasContent) {
            bestEntries.set(key, entry);
          }
        }
      }

      const deduped = Array.from(bestEntries.values());

      // 7. Sort by timestamp descending
      deduped.sort((a: any, b: any) => {
        const dateA = new Date(a.timestamp || 0).getTime();
        const dateB = new Date(b.timestamp || 0).getTime();
        return dateB - dateA;
      });

      return deduped;
    } catch (error) {
      console.error('Error fetching activity log:', error);
      return [];
    }
  },

  // Get email tracking records for a customer
  getCustomerEmailTracking: async (customerId: string): Promise<any[]> => {
    try {
      const q = query(
        collection(db, 'emailTracking'),
        where('customerId', '==', customerId),
        orderBy('sentAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching email tracking:', error);
      return [];
    }
  },

  // Log an activity entry
  logActivity: async (entry: {
    customerId: string;
    type: string;
    actor?: string;
    details?: Record<string, any>;
  }): Promise<void> => {
    await addDoc(collection(db, 'activityLog'), {
      ...entry,
      timestamp: new Date().toISOString()
    });
  },

  // Generate Legal Evidence PDF for court/police
  generateLegalEvidencePDF: async (customer: any): Promise<void> => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPos = 20;
    const lineHeight = 7;
    const margin = 20;
    const textWidth = pageWidth - (margin * 2);

    // Helper: Add section header
    const addHeader = (text: string) => {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(30, 41, 59); // slate-800
      pdf.text(text, margin, yPos);
      yPos += lineHeight + 3;
      pdf.setDrawColor(100, 116, 139);
      pdf.line(margin, yPos - 3, pageWidth - margin, yPos - 3);
      yPos += 5;
    };

    // Helper: Add text line
    const addLine = (label: string, value: string) => {
      if (yPos > 270) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(71, 85, 105);
      pdf.text(label + ":", margin, yPos);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(30, 41, 59);
      pdf.text(value || "N/A", margin + 50, yPos);
      yPos += lineHeight;
    };

    // --- COVER PAGE ---
    pdf.setFontSize(24);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(127, 29, 29); // red-800
    pdf.text("LEGAL EVIDENCE FOLDER", pageWidth / 2, 60, { align: "center" });

    pdf.setFontSize(16);
    pdf.setTextColor(30, 41, 59);
    pdf.text(customer.applicantName || "Unknown Customer", pageWidth / 2, 80, { align: "center" });

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 95, { align: "center" });
    pdf.text(`Account ID: ${customer.id}`, pageWidth / 2, 105, { align: "center" });

    if (customer.status === 'LEGAL_THEFT' || customer.status === 'TERMINATED') {
      pdf.setTextColor(220, 38, 38);
      pdf.setFont("helvetica", "bold");
      pdf.text(`STATUS: ${customer.status}`, pageWidth / 2, 120, { align: "center" });
    }

    pdf.setTextColor(100, 116, 139);
    pdf.setFontSize(10);
    pdf.text("NestlySuite Inc. | Appliance Rental Services", pageWidth / 2, 250, { align: "center" });
    pdf.text("This document is for legal proceedings and law enforcement use.", pageWidth / 2, 258, { align: "center" });

    // --- PAGE 2: CUSTOMER INFORMATION ---
    pdf.addPage();
    yPos = 20;

    addHeader("1. CUSTOMER INFORMATION");
    addLine("Full Name", customer.applicantName);
    addLine("Email", customer.email);
    addLine("Phone", customer.phone);
    addLine("Address", customer.address);
    addLine("Status", customer.status);
    addLine("Account Created", customer.submissionDate ? new Date(customer.submissionDate).toLocaleDateString() : "N/A");

    yPos += 10;
    addHeader("2. EQUIPMENT DETAILS");
    addLine("Serial Number", customer.equipmentSerialNumber || "Not assigned");
    addLine("Equipment Type", "Washer/Dryer Combo Unit");
    addLine("Equipment Value", "$700.00");
    addLine("Returned", customer.equipmentReturned ? "Yes" : "No");

    yPos += 10;
    addHeader("3. CONTRACT DETAILS");
    addLine("Lease Start", customer.leaseStartDate ? new Date(customer.leaseStartDate).toLocaleDateString() : "N/A");
    addLine("Lease End", customer.leaseEndDate ? new Date(customer.leaseEndDate).toLocaleDateString() : "N/A");
    addLine("Monthly Rent", "$40.00");
    addLine("Lease Signed", customer.leaseSignedDate ? new Date(customer.leaseSignedDate).toLocaleDateString() : "N/A");

    yPos += 10;
    addHeader("4. TERMINATION DETAILS");
    addLine("Terminated On", customer.terminatedAt ? new Date(customer.terminatedAt).toLocaleString() : "Not terminated");
    addLine("Reason", customer.terminationReason || "N/A");
    addLine("Pickup Deadline", customer.pickupScheduledDate ? new Date(customer.pickupScheduledDate).toLocaleString() : "N/A");
    addLine("Theft Reported", customer.theftReportedAt ? new Date(customer.theftReportedAt).toLocaleString() : "Not yet reported");
    addLine("Report Number", customer.theftReportNumber || "N/A");

    // --- PAGE 3: PAYMENT HISTORY ---
    pdf.addPage();
    yPos = 20;
    addHeader("5. PAYMENT HISTORY");

    addLine("Stripe Customer ID", customer.stripeCustomerId || "Not linked");
    addLine("Payment Status", customer.paymentStatus || "Unknown");
    addLine("Last Payment", customer.lastPaymentDate ? new Date(customer.lastPaymentDate).toLocaleDateString() : "Never");
    addLine("Last Failure", customer.lastPaymentFailureDate ? new Date(customer.lastPaymentFailureDate).toLocaleDateString() : "None");

    if (customer.purchaseInvoicedAt) {
      yPos += 10;
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(220, 38, 38);
      pdf.text("EQUIPMENT PURCHASE INVOICE", margin, yPos);
      yPos += lineHeight;
      addLine("Invoice Date", new Date(customer.purchaseInvoicedAt).toLocaleString());
      addLine("Invoice Amount", `$${customer.purchaseInvoiceAmount?.toFixed(2) || "700.00"}`);
    }

    // --- PAGE 4: LEASE AGREEMENT ---
    pdf.addPage();
    yPos = 20;
    addHeader("6. SIGNED LEASE AGREEMENT");

    if (customer.leaseContent) {
      pdf.setFont("courier", "normal");
      pdf.setFontSize(8);
      const leaseLines = pdf.splitTextToSize(customer.leaseContent, textWidth);
      for (const line of leaseLines) {
        if (yPos > 275) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(line, margin, yPos);
        yPos += 4;
      }
    } else {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(10);
      pdf.text("Lease content not available in record.", margin, yPos);
    }

    // --- FETCH EMAIL TRACKING DATA ---
    const emailTracking = await backendService.getCustomerEmailTracking(customer.id);

    if (emailTracking.length > 0) {
      pdf.addPage();
      yPos = 20;
      addHeader("7. EMAIL COMMUNICATIONS (With Read Receipts)");

      for (const email of emailTracking) {
        if (yPos > 240) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(30, 41, 59);
        pdf.text(`Type: ${email.type}`, margin, yPos);
        yPos += lineHeight;

        pdf.setFont("helvetica", "normal");
        pdf.text(`Subject: ${email.subject}`, margin, yPos);
        yPos += lineHeight;
        pdf.text(`Sent: ${new Date(email.sentAt).toLocaleString()}`, margin, yPos);
        yPos += lineHeight;

        // Highlight if opened
        if (email.status === 'OPENED') {
          pdf.setTextColor(22, 163, 74); // green-600
          pdf.setFont("helvetica", "bold");
          pdf.text(`OPENED: ${new Date(email.openedAt).toLocaleString()} ✓`, margin, yPos);
        } else {
          pdf.setTextColor(220, 38, 38);
          pdf.text(`Status: ${email.status} (Not confirmed opened)`, margin, yPos);
        }
        yPos += lineHeight + 5;
        pdf.setTextColor(30, 41, 59);

        // Draw separator
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
      }
    }

    // --- ACTIVITY LOG ---
    const activityLog = await backendService.getCustomerActivityLog(customer.id);

    if (activityLog.length > 0) {
      pdf.addPage();
      yPos = 20;
      addHeader("8. ACTIVITY LOG");

      for (const entry of activityLog) {
        if (yPos > 265) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.text(`${new Date(entry.timestamp).toLocaleString()} - ${entry.type}`, margin, yPos);
        yPos += 5;
        if (entry.details?.notes) {
          pdf.text(`  ${entry.details.notes}`, margin, yPos);
          yPos += 5;
        }
        yPos += 3;
      }
    }

    // --- FOOTER ON ALL PAGES ---
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: "center" });
      pdf.text(`NestlySuite Inc. Legal Evidence - ${customer.applicantName}`, margin, 290);
    }

    // Save the PDF
    pdf.save(`LegalEvidence_${customer.applicantName?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  },

  // ========================================================================
  // LEASE TEMPLATE VERSIONING
  // ========================================================================

  // Get all lease templates (versions)
  getLeaseTemplates: async (): Promise<any[]> => {
    const q = query(collection(db, 'leaseTemplates'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // Get active lease template
  getActiveLeaseTemplate: async (): Promise<any | null> => {
    const q = query(collection(db, 'leaseTemplates'), where('isActive', '==', true));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  },

  // Get lease template by ID
  getLeaseTemplateById: async (templateId: string): Promise<any | null> => {
    const docRef = doc(db, 'leaseTemplates', templateId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  },

  // Save new lease template version (auto-increments version number)
  saveLeaseTemplate: async (content: string, adminEmail: string, notes?: string): Promise<string> => {
    // Get all templates to determine next version
    const templates = await backendService.getLeaseTemplates();

    // Determine next version number
    let nextVersion = 'v1.0';
    if (templates.length > 0) {
      const versions = templates.map(t => t.version || 'v1.0');
      const latestVersion = versions[0]; // Already sorted by createdAt desc
      const match = latestVersion.match(/v(\d+)\.(\d+)/);
      if (match) {
        const major = parseInt(match[1]);
        const minor = parseInt(match[2]) + 1;
        nextVersion = `v${major}.${minor}`;
      }
    }

    // Deactivate all existing templates
    for (const template of templates) {
      if (template.isActive) {
        await updateDoc(doc(db, 'leaseTemplates', template.id), { isActive: false });
      }
    }

    // Create new template
    const newTemplate = {
      version: nextVersion,
      content,
      createdAt: new Date().toISOString(),
      createdBy: adminEmail,
      isActive: true,
      notes: notes || `Version ${nextVersion} created by ${adminEmail}`
    };

    const docRef = await addDoc(collection(db, 'leaseTemplates'), newTemplate);
    return docRef.id;
  },

  // Push lease update to all active customers
  pushLeaseToAllCustomers: async (templateId: string, adminEmail: string): Promise<{ success: number; failed: number }> => {
    // Get the template
    const template = await backendService.getLeaseTemplateById(templateId);
    if (!template) throw new Error('Template not found');

    // Update template with push info
    await updateDoc(doc(db, 'leaseTemplates', templateId), {
      pushedAt: new Date().toISOString(),
      pushedBy: adminEmail
    });

    // Get all active customers (ACTIVE or INSTALLED status)
    const q = query(
      collection(db, 'applications'),
      where('status', 'in', [AppStatus.ACTIVE, AppStatus.INSTALLED])
    );
    const snap = await getDocs(q);

    let success = 0;
    let failed = 0;

    for (const docSnap of snap.docs) {
      try {
        const app = docSnap.data();

        // Generate unique acceptance token for this customer
        const acceptanceToken = `${docSnap.id}_${template.version}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        const tokenExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days

        // Set pending lease version and acceptance token
        await updateDoc(doc(db, 'applications', docSnap.id), {
          pendingLeaseVersion: template.version,
          pendingLeaseTemplateId: templateId,
          leaseAcceptanceToken: acceptanceToken,
          leaseAcceptanceTokenExpiry: tokenExpiry
        });

        // Build direct acceptance link
        const acceptanceLink = `${window.location.origin}/accept-lease?token=${encodeURIComponent(acceptanceToken)}`;

        // Send notification email with direct link
        await addDoc(collection(db, 'mail'), {
          to: app.email,
          subject: '📋 Important Update to Your Lease Terms - Action Required',
          html: `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #1e293b; margin-bottom: 10px;">Lease Agreement Updated</h1>
                <p style="color: #64748b;">${template.version}</p>
              </div>
              
              <p style="color: #334155;">Dear ${app.applicantName},</p>
              
              <p style="color: #334155;">We have updated our Appliance Rental Agreement to better serve you. 
              Please review and acknowledge the updated terms by clicking the button below.</p>
              
              <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 20px; border-radius: 12px; margin: 20px 0; border-left: 4px solid #f59e0b;">
                <p style="color: #92400e; margin: 0;">
                  <strong>⚠️ Action Required:</strong> You must review and accept the updated lease terms to 
                  continue using our services. This link expires in 30 days.
                </p>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${acceptanceLink}" 
                   style="background: linear-gradient(135deg, #059669 0%, #10b981 100%); 
                          color: white; padding: 16px 40px; border-radius: 8px; 
                          text-decoration: none; font-weight: 600; display: inline-block;
                          font-size: 16px; box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);">
                  ✅ Review & Accept Updated Terms
                </a>
              </div>
              
              <p style="color: #64748b; font-size: 14px; text-align: center;">
                No login required - just click the button above to review and accept.
              </p>
              
              <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 30px 0;">
              
              <p style="color: #64748b; font-size: 13px;">
                If you have any questions about the updated terms, please contact our support team 
                at support@nestlysuite.com
              </p>
              
              <p style="color: #94a3b8; font-size: 12px; text-align: center; margin-top: 20px;">
                NestlySuite Inc. | Appliance Rental Services<br>
                <span style="font-size: 11px;">This link is unique to your account. Do not share it.</span>
              </p>
            </div>
          `,
          createdAt: new Date().toISOString()
        });

        success++;
      } catch (err) {
        console.error('Failed to push lease to customer:', docSnap.id, err);
        failed++;
      }
    }

    return { success, failed };
  },

  // Push lease update to selected customers (by ID)
  pushLeaseToSelectedCustomers: async (
    templateId: string,
    customerIds: string[],
    adminEmail: string
  ): Promise<{ success: number; failed: number }> => {
    // Get the template
    const template = await backendService.getLeaseTemplateById(templateId);
    if (!template) throw new Error('Template not found');

    let success = 0;
    let failed = 0;

    for (const customerId of customerIds) {
      try {
        // Get customer data
        const appDoc = await getDoc(doc(db, 'applications', customerId));
        if (!appDoc.exists()) {
          console.warn('Customer not found:', customerId);
          failed++;
          continue;
        }
        const app = appDoc.data();

        // Generate unique acceptance token
        const acceptanceToken = `${customerId}_${template.version}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        const tokenExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        // Set pending lease version and token
        await updateDoc(doc(db, 'applications', customerId), {
          pendingLeaseVersion: template.version,
          pendingLeaseTemplateId: templateId,
          leaseAcceptanceToken: acceptanceToken,
          leaseAcceptanceTokenExpiry: tokenExpiry
        });

        // Build acceptance link
        const acceptanceLink = `${window.location.origin}/accept-lease?token=${encodeURIComponent(acceptanceToken)}`;

        // Send email
        await addDoc(collection(db, 'mail'), {
          to: app.email,
          subject: '📋 Important Update to Your Lease Terms - Action Required',
          html: `
            <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #1e293b; margin-bottom: 10px;">Lease Agreement Updated</h1>
                <p style="color: #64748b;">${template.version}</p>
              </div>
              
              <p style="color: #334155;">Dear ${app.applicantName},</p>
              
              <p style="color: #334155;">We have updated our Appliance Rental Agreement. 
              Please review and acknowledge the updated terms by clicking the button below.</p>
              
              <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 20px; border-radius: 12px; margin: 20px 0; border-left: 4px solid #f59e0b;">
                <p style="color: #92400e; margin: 0;">
                  <strong>⚠️ Action Required:</strong> Please review and accept the updated lease terms. 
                  This link expires in 30 days.
                </p>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${acceptanceLink}" 
                   style="background: linear-gradient(135deg, #059669 0%, #10b981 100%); 
                          color: white; padding: 16px 40px; border-radius: 8px; 
                          text-decoration: none; font-weight: 600; display: inline-block;">
                  ✅ Review & Accept Updated Terms
                </a>
              </div>
              
              <p style="color: #94a3b8; font-size: 12px; text-align: center;">
                NestlySuite Inc. | No login required - just click the button above.
              </p>
            </div>
          `,
          createdAt: new Date().toISOString()
        });

        success++;
      } catch (err) {
        console.error('Failed to push lease to customer:', customerId, err);
        failed++;
      }
    }

    return { success, failed };
  },

  // Record lease acceptance (audit trail)
  recordLeaseAcceptance: async (
    appId: string,
    templateId: string,
    templateVersion: string,
    userId: string,
    userEmail: string,
    ipAddress: string,
    userAgent: string
  ): Promise<void> => {
    // Add acceptance record to subcollection
    await addDoc(collection(db, 'applications', appId, 'leaseAcceptances'), {
      leaseTemplateId: templateId,
      leaseVersion: templateVersion,
      acceptedAt: new Date().toISOString(),
      userId,
      userEmail,
      ipAddress,
      userAgent
    });

    // Update application to clear pending and set current version
    await updateDoc(doc(db, 'applications', appId), {
      pendingLeaseVersion: null,
      currentLeaseVersion: templateVersion
    });

    // Add activity log entry
    await addDoc(collection(db, 'applications', appId, 'activityLog'), {
      type: 'LEASE_ACCEPTED',
      timestamp: new Date().toISOString(),
      actor: userEmail,
      details: {
        leaseVersion: templateVersion,
        ipAddress,
        notes: `Accepted lease ${templateVersion} via click-wrap agreement`
      }
    });
  },

  // Get lease acceptances for a customer
  getLeaseAcceptances: async (appId: string): Promise<any[]> => {
    const q = query(
      collection(db, 'applications', appId, 'leaseAcceptances'),
      orderBy('acceptedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  // Validate lease acceptance token (for public access)
  validateLeaseAcceptanceToken: async (token: string): Promise<{
    valid: boolean;
    customer?: { id: string; name: string; email: string; address: string };
    lease?: { templateId: string; version: string; content: string };
    error?: string;
  }> => {
    try {
      // Find customer with this token
      const q = query(
        collection(db, 'applications'),
        where('leaseAcceptanceToken', '==', token)
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        return { valid: false, error: 'Invalid or expired token' };
      }

      const customerDoc = snap.docs[0];
      const customer = customerDoc.data();

      // Check token expiry
      const expiry = customer.leaseAcceptanceTokenExpiry;
      if (expiry && new Date(expiry) < new Date()) {
        return { valid: false, error: 'Token has expired. Please contact support.' };
      }

      // Check if already accepted
      if (!customer.pendingLeaseVersion) {
        return { valid: false, error: 'Lease has already been accepted' };
      }

      // Get the lease template
      const templateId = customer.pendingLeaseTemplateId;
      const template = await backendService.getLeaseTemplateById(templateId);
      if (!template) {
        return { valid: false, error: 'Lease template not found' };
      }

      return {
        valid: true,
        customer: {
          id: customerDoc.id,
          name: customer.applicantName,
          email: customer.email,
          address: customer.address || 'Address on file'
        },
        lease: {
          templateId: template.id,
          version: template.version,
          content: template.content
        }
      };
    } catch (err) {
      console.error('Error validating token:', err);
      return { valid: false, error: 'An error occurred validating the token' };
    }
  },

  // Record public lease acceptance (no login required)
  recordPublicLeaseAcceptance: async (
    token: string,
    ipAddress: string,
    userAgent: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Validate the token first
      const validation = await backendService.validateLeaseAcceptanceToken(token);
      if (!validation.valid || !validation.customer || !validation.lease) {
        return { success: false, error: validation.error || 'Invalid token' };
      }

      const { customer, lease } = validation;

      // Auto admin signature data
      const adminAutoSignature = {
        signedBy: 'NestlySuite Inc.',
        signedAt: new Date().toISOString(),
        signatureType: 'auto',
        notes: 'Automatically counter-signed upon customer acceptance'
      };

      // Add acceptance record to subcollection with admin auto-signature
      await addDoc(collection(db, 'applications', customer.id, 'leaseAcceptances'), {
        leaseTemplateId: lease.templateId,
        leaseVersion: lease.version,
        acceptedAt: new Date().toISOString(),
        userId: customer.id,
        userEmail: customer.email,
        ipAddress,
        userAgent,
        acceptanceMethod: 'public_link',
        // Admin auto-signature
        adminSignature: adminAutoSignature.signedBy,
        adminSignedAt: adminAutoSignature.signedAt,
        adminSignatureType: 'auto'
      });

      // Update application to clear pending, set current, invalidate token, and record admin signature
      await updateDoc(doc(db, 'applications', customer.id), {
        pendingLeaseVersion: null,
        pendingLeaseTemplateId: null,
        currentLeaseVersion: lease.version,
        currentLeaseSignedAt: new Date().toISOString(),
        leaseAcceptanceToken: null,
        leaseAcceptanceTokenExpiry: null,
        // Admin auto-signature on current lease
        adminAutoSignature: adminAutoSignature.signedBy,
        adminAutoSignedAt: adminAutoSignature.signedAt
      });

      // Add activity log entry
      await addDoc(collection(db, 'applications', customer.id, 'activityLog'), {
        type: 'LEASE_ACCEPTED',
        timestamp: new Date().toISOString(),
        actor: customer.email,
        details: {
          leaseVersion: lease.version,
          ipAddress,
          notes: `Accepted lease ${lease.version} via public email link`
        }
      });

      return { success: true };
    } catch (err) {
      console.error('Error recording public lease acceptance:', err);
      return { success: false, error: 'Failed to record acceptance' };
    }
  },

  // Get default lease template content - COMPLETE LEASE with all sections
  // Uses centralized template with placeholder tokens for dynamic replacement
  getDefaultLeaseTemplateContent: (): string => {
    // Generate lease with placeholder values, then convert to HTML
    const leaseText = generateApplianceLease({
      customerName: '{{CUSTOMER_NAME}}',
      customerEmail: '{{CUSTOMER_EMAIL}}',
      customerPhone: '{{CUSTOMER_PHONE}}',
      customerAddress: '{{CUSTOMER_ADDRESS}}',
      equipmentDescription: '{{EQUIPMENT_DESCRIPTION}}',
      equipmentSerialNumber: '{{SERIAL_NUMBER}}',
      equipmentValue: 700,
      monthlyRent: 50,
      minimumTerm: 6,
      startDate: new Date('{{START_DATE}}'),
      endDate: new Date('{{END_DATE}}')
    });

    // Convert plain text to HTML with proper formatting
    const htmlContent = leaseText
      .replace(/═+/g, '<hr style="border: 2px solid #1e3a5f; margin: 15px 0;">')
      .replace(/^(\d+\. [A-Z &]+)$/gm, '<h3 style="color: #1e3a5f; margin-top: 20px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">$1</h3>')
      .replace(/^([a-h]\. [A-Z ]+:)/gm, '<p style="margin: 8px 0;"><strong>$1</strong></p>')
      .replace(/• /g, '&bull; ')
      .replace(/\n\n/g, '</p><p style="margin: 10px 0;">')
      .replace(/\n/g, '<br>');

    // Fix the invalid dates from placeholder strings
    const fixedHtml = htmlContent
      .replace(/Invalid Date/g, '{{START_DATE}}')
      .replace(/NaN/g, '');

    return `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">
  <h2 style="text-align: center; color: #1e3a5f;">APPLIANCE RENTAL AGREEMENT</h2>
  <p style="margin: 10px 0;">${fixedHtml}</p>
</div>
`.trim();
  },

  // Delete an application permanently
  async deleteApplication(id: string): Promise<void> {
    await deleteDoc(doc(db, 'applications', id));
  },

  /**
   * Send a legal notice to a customer for collections purposes.
   * Logs all communications for legal evidence.
   */
  async sendLegalNotice(
    customerId: string,
    noticeType: 'NOTICE_DEFAULT' | 'TERMINATION_DEMAND' | 'PICKUP_DEMAND' | 'THEFT_WARNING'
  ): Promise<{ success: boolean; emailId: string }> {
    // Get customer details
    const customer = await this.getApplicationById(customerId);
    if (!customer) throw new Error('Customer not found');

    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Define notice templates based on lease terms
    const notices = {
      NOTICE_DEFAULT: {
        subject: 'NOTICE OF DEFAULT - Appliance Rental Agreement',
        collectionsStatus: 'DEFAULT_NOTICE_SENT',
        content: `
          <p>Dear ${customer.applicantName},</p>
          <p><strong>RE: Notice of Default - Appliance Rental Agreement</strong></p>
          <p>This letter serves as formal notice that your account with Nestly Suite is currently in default due to non-payment.</p>
          <p><strong>Property Address:</strong> ${customer.address}</p>
          <p><strong>Date of Notice:</strong> ${formattedDate}</p>
          <h3>REQUIRED ACTION:</h3>
          <p>Per your Rental Agreement, you have <strong>30 days</strong> from the date of this notice to:</p>
          <ol>
            <li>Pay all outstanding amounts owed, OR</li>
            <li>Contact us to arrange equipment pickup</li>
          </ol>
          <h3>EARLY TERMINATION TERMS (Per Your Agreement):</h3>
          <p>If you wish to terminate your rental agreement:</p>
          <ul>
            <li>You may terminate with 30 days written notice</li>
            <li>Termination prior to the Minimum Term incurs an <strong>Early Termination Fee of $100.00</strong></li>
            <li>This fee covers appliance pickup and administrative costs</li>
          </ul>
          <h3>LESSOR RIGHTS:</h3>
          <p>Per your agreement, Lessor may terminate with 30 days written notice for non-payment, breach of terms, or damage/misuse.</p>
          <p>Failure to respond to this notice may result in equipment retrieval, termination of service, and referral to collections.</p>
          <p>Please contact us immediately at admin@nestlysuite.com to resolve this matter.</p>
          <p>Sincerely,<br>Nestly Suite Management</p>
        `
      },
      TERMINATION_DEMAND: {
        subject: 'NOTICE OF LEASE TERMINATION - Immediate Action Required',
        collectionsStatus: 'TERMINATED',
        content: `
          <p>Dear ${customer.applicantName},</p>
          <p><strong>RE: NOTICE OF LEASE TERMINATION</strong></p>
          <p style="color: red; font-weight: bold;">Your Appliance Rental Agreement has been TERMINATED effective immediately due to non-payment and failure to respond to previous notices.</p>
          <p><strong>Property Address:</strong> ${customer.address}</p>
          <p><strong>Date of Termination:</strong> ${formattedDate}</p>
          <h3>IMMEDIATE ACTION REQUIRED:</h3>
          <ol>
            <li>Schedule equipment pickup within 72 hours</li>
            <li>Pay all outstanding balances including the $100 Early Termination Fee</li>
            <li>Ensure equipment is in good working condition</li>
          </ol>
          <h3>CONSEQUENCES OF NON-COMPLIANCE:</h3>
          <ul>
            <li>Equipment retrieval will be scheduled without further notice</li>
            <li>Outstanding debt will be referred to collections</li>
            <li>Your account may be reported to credit bureaus</li>
            <li>Legal action may be pursued for recovery of equipment and damages</li>
          </ul>
          <p>Contact us immediately at admin@nestlysuite.com or call to arrange pickup.</p>
          <p>This is your final notice.</p>
          <p>Sincerely,<br>Nestly Suite Management</p>
        `
      },
      PICKUP_DEMAND: {
        subject: 'EQUIPMENT PICKUP DEMAND - Schedule Required',
        collectionsStatus: 'PICKUP_DEMANDED',
        content: `
          <p>Dear ${customer.applicantName},</p>
          <p><strong>RE: Equipment Pickup Demand</strong></p>
          <p>Your Appliance Rental Agreement has been terminated. We must now schedule pickup of our equipment.</p>
          <p><strong>Property Address:</strong> ${customer.address}</p>
          <p><strong>Equipment Serial Number:</strong> ${customer.equipmentSerialNumber || 'On file'}</p>
          <h3>PICKUP SCHEDULING:</h3>
          <p>Please contact us within <strong>48 hours</strong> to schedule a pickup time. Our available pickup times are Monday-Friday, 9am-5pm.</p>
          <h3>EQUIPMENT REQUIREMENTS:</h3>
          <ul>
            <li>Equipment must be accessible and ready for pickup</li>
            <li>All hoses, power cords, and accessories must be included</li>
            <li>Equipment must be in reasonable working condition</li>
          </ul>
          <h3>IMPORTANT:</h3>
          <p>Failure to schedule pickup or provide access will result in:</p>
          <ul>
            <li>Additional recovery fees</li>
            <li>Police report for theft of property</li>
            <li>Civil action for equipment value</li>
          </ul>
          <p>Contact: admin@nestlysuite.com</p>
          <p>Sincerely,<br>Nestly Suite Management</p>
        `
      },
      THEFT_WARNING: {
        subject: '⚠️ FINAL WARNING - Theft Report Will Be Filed',
        collectionsStatus: 'THEFT_WARNING_SENT',
        content: `
          <p>Dear ${customer.applicantName},</p>
          <p style="background: #fef2f2; border: 2px solid #dc2626; padding: 16px; color: #991b1b;">
            <strong>⚠️ FINAL WARNING - THEFT OF PROPERTY</strong>
          </p>
          <p>Despite multiple attempts to contact you regarding the return of our equipment, you have failed to respond or schedule pickup.</p>
          <p><strong>Property Address:</strong> ${customer.address}</p>
          <p><strong>Equipment Serial Number:</strong> ${customer.equipmentSerialNumber || 'On file'}</p>
          <p><strong>Equipment Value:</strong> $800.00+</p>
          <h3 style="color: #dc2626;">FINAL NOTICE:</h3>
          <p>If we do not hear from you within <strong>72 HOURS</strong> of this notice, we will:</p>
          <ol>
            <li><strong>File a police report</strong> for theft of property</li>
            <li><strong>Report the debt</strong> to credit bureaus</li>
            <li><strong>Sell the debt</strong> to a collections agency</li>
            <li><strong>Pursue civil action</strong> for full equipment value plus legal fees</li>
          </ol>
          <p>This is your <strong>FINAL OPPORTUNITY</strong> to resolve this matter before legal action is taken.</p>
          <p>Contact us immediately: admin@nestlysuite.com</p>
          <p>Sincerely,<br>Nestly Suite Management</p>
        `
      }
    };

    const notice = notices[noticeType];
    if (!notice) throw new Error('Invalid notice type');

    // Generate full email HTML
    const emailHtml = this.generateEmailHtml(
      notice.subject,
      notice.content,
      'https://www.nestlysuite.com/#/login',
      'Contact Us'
    );

    // Send email with tracking
    await this.triggerEmail(
      customer.email,
      notice.subject,
      emailHtml,
      customerId
    );

    // Update customer's collections status
    await updateDoc(doc(db, 'applications', customerId), {
      collectionsStatus: notice.collectionsStatus,
      lastNoticeType: noticeType,
      lastNoticeSentAt: now.toISOString(),
      paymentStatus: 'UNPAID',
      updatedAt: now.toISOString()
    });

    // Log activity for legal evidence
    await addDoc(collection(db, 'applications', customerId, 'activityLog'), {
      type: 'EMAIL_SENT',
      timestamp: now.toISOString(),
      actor: 'admin@nestlysuite.com',
      details: {
        subject: notice.subject,
        noticeType: noticeType,
        to: customer.email,
        htmlContent: emailHtml,
        status: 'SUCCESS'
      }
    });

    // Send admin notification
    await this.triggerEmail(
      'admin@nestlysuite.com',
      `Legal Notice Sent: ${noticeType} - ${customer.applicantName}`,
      this.generateEmailHtml(
        'Legal Notice Confirmation',
        `<p>A ${noticeType.replace(/_/g, ' ')} was sent to:</p>
         <p><strong>Customer:</strong> ${customer.applicantName}</p>
         <p><strong>Email:</strong> ${customer.email}</p>
         <p><strong>Address:</strong> ${customer.address}</p>
         <p><strong>Time:</strong> ${now.toLocaleString()}</p>
         <p>This email is saved for legal evidence purposes.</p>`
      )
    );

    return { success: true, emailId: `notice_${customerId}_${Date.now()}` };
  },

  /**
   * Send a legal notice with custom content (editable before sending).
   * Used when admin wants to modify the notice text.
   */
  async sendLegalNoticeCustom(
    customerId: string,
    noticeType: 'NOTICE_DEFAULT' | 'TERMINATION_DEMAND' | 'PICKUP_DEMAND' | 'THEFT_WARNING',
    subject: string,
    content: string
  ): Promise<{ success: boolean; emailId: string }> {
    // Get customer details
    const customer = await this.getApplicationById(customerId);
    if (!customer) throw new Error('Customer not found');

    const now = new Date();

    // Map notice types to collections status
    const statusMap: Record<string, string> = {
      'NOTICE_DEFAULT': 'DEFAULT_NOTICE_SENT',
      'TERMINATION_DEMAND': 'TERMINATED',
      'PICKUP_DEMAND': 'PICKUP_DEMANDED',
      'THEFT_WARNING': 'THEFT_WARNING_SENT'
    };

    // Convert plain text content to HTML (preserve line breaks)
    const htmlContent = content
      .split('\n')
      .map(line => line.trim() === '' ? '<br>' : `<p>${line}</p>`)
      .join('');

    // Generate full email HTML
    const emailHtml = this.generateEmailHtml(
      subject,
      htmlContent,
      'https://www.nestlysuite.com/#/login',
      'Contact Us'
    );

    // Send email with tracking
    await this.triggerEmail(
      customer.email,
      subject,
      emailHtml,
      customerId
    );

    // Update customer's collections status
    await updateDoc(doc(db, 'applications', customerId), {
      collectionsStatus: statusMap[noticeType] || 'NOTICE_SENT',
      lastNoticeType: noticeType,
      lastNoticeSentAt: now.toISOString(),
      paymentStatus: 'UNPAID',
      updatedAt: now.toISOString()
    });

    // Log activity for legal evidence (with full custom content)
    await addDoc(collection(db, 'applications', customerId, 'activityLog'), {
      type: 'EMAIL_SENT',
      timestamp: now.toISOString(),
      actor: 'admin@nestlysuite.com',
      details: {
        subject: subject,
        noticeType: noticeType,
        to: customer.email,
        textContent: content, // Store the editable text version
        htmlContent: emailHtml,
        status: 'SUCCESS'
      }
    });

    // Send admin notification
    await this.triggerEmail(
      'admin@nestlysuite.com',
      `Legal Notice Sent: ${noticeType} - ${customer.applicantName}`,
      this.generateEmailHtml(
        'Legal Notice Confirmation',
        `<p>A ${noticeType.replace(/_/g, ' ')} was sent to:</p>
         <p><strong>Customer:</strong> ${customer.applicantName}</p>
         <p><strong>Email:</strong> ${customer.email}</p>
         <p><strong>Address:</strong> ${customer.address}</p>
         <p><strong>Subject:</strong> ${subject}</p>
         <p><strong>Time:</strong> ${now.toLocaleString()}</p>
         <p>This email is saved for legal evidence purposes.</p>`
      )
    );

    return { success: true, emailId: `notice_${customerId}_${Date.now()}` };
  },

  // --- Ohio Sales Tax Compliance ---
  getTaxSummary: async (): Promise<any> => {
    try {
      const fn = httpsCallable(functions, 'getTaxSummary');
      const result = await fn({});
      return (result.data as any);
    } catch (error: any) {
      console.error('Error fetching tax summary:', error);
      // Fallback: read directly from Firestore if callable fails
      try {
        const snap = await getDocs(collection(db, 'taxFilings'));
        const filings = snap.docs.map(doc => {
          const data = doc.data();
          // Compute status dynamically
          let status = 'COLLECTING';
          if (data.paidDate) status = 'PAID';
          else if (new Date() > new Date(data.dueDate)) status = 'OVERDUE';
          else if (new Date() > new Date(data.periodEnd)) status = 'DUE';
          return { id: doc.id, ...data, status };
        }).sort((a: any, b: any) => b.periodStart?.localeCompare(a.periodStart));
        return { success: true, filings };
      } catch (fbErr) {
        console.error('Firestore fallback failed:', fbErr);
        return { success: false, filings: [] };
      }
    }
  },

  markTaxFiled: async (data: {
    period: string;
    amountRemitted: number;
    paidDate: string;
    confirmationNumber?: string;
    notes?: string;
  }): Promise<any> => {
    try {
      const fn = httpsCallable(functions, 'markTaxFiled');
      const result = await fn(data);
      return (result.data as any);
    } catch (error: any) {
      console.error('Error marking tax filed:', error);
      throw error;
    }
  },
};